import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import './App.css';
import HierarchicalProcedureSelector from './HierarchicalProcedureSelector';
import HierarchicalSurgicalProcedureSelector from './HierarchicalSurgicalProcedureSelector';
import SummaryView from './SummaryView';
import BloodVolumeCalculator from './components/BloodVolumeCalculator';
import AlternativeSearchManager from './components/AlternativeSearchManager';
import GuidedConversation from './components/GuidedConversation/GuidedConversation';
import AgentChat from './components/AgentChat/AgentChat';
import FeedbackModal from './components/FeedbackModal';
import FeedbackDashboard from './components/FeedbackDashboard';
import SettingsView from './components/Settings/SettingsView';
import { ApiKeyInfo, FreeTierInfo } from './components/Settings/types';

// API configuration - Railway uses full URL, Codespace uses relative URLs with proxy
const API_BASE_URL = process.env.REACT_APP_API_URL || '';

// Define types for IACUC form
interface User {
  id: number;
  email: string;
  name: string;
  institution?: string;
  department?: string;
  has_seen_welcome?: boolean;
  is_admin?: boolean;
}

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  needsWelcome: boolean;
}

interface Protocol {
  id: number;
  title: string;
  version: number;
  status: string;
  current_question: string;
  created_at: string;
  updated_at: string;
  user_id: number;
  answers?: Answer[];
  entry_mode?: 'protocol_view' | 'guided_conversation';
  guided_complete?: boolean;
}

interface Question {
  question_number: string;
  question_text: string;
  question_type: 'text' | 'yes_no' | 'complex' | string;
  requirements?: string;
  example_answers?: any; // Changed to any to handle followup_config
}

interface Answer {
  question_number: string;
  answer: string;
  version: number;
  created_at: string;
}

interface ExampleAnswer {
  title: string;
  content: string;
}

function App() {
  // Authentication state
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    token: null,
    isAuthenticated: false,
    needsWelcome: false
  });
  const [protocols, setProtocols] = useState<Protocol[]>([]);
  const [currentProtocol, setCurrentProtocol] = useState<Protocol | null>(null);
  const [currentSection, setCurrentSection] = useState<number | 'summary'>(1);
  const [sectionQuestions, setSectionQuestions] = useState<Question[]>([]);
  const [sectionAnswers, setSectionAnswers] = useState<{[key: string]: {answer: string, followupData: any[], acknowledgements?: string[], cfaJustification?: string}}>({});
  const [aiSuggestions, setAiSuggestions] = useState<{[key: string]: string}>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState<{[key: string]: boolean}>({});
  const [showExamples, setShowExamples] = useState<{[key: string]: boolean}>({});
  const [question42Mode, setQuestion42Mode] = useState<'free_text' | 'structured'>('free_text');
  const [question42State, setQuestion42State] = useState<'choosing' | 'building' | 'final'>('choosing');
  const [showStructuredBuilder, setShowStructuredBuilder] = useState(false);
  const [structuredComponents, setStructuredComponents] = useState<{[key: string]: {[componentType: string]: string}}>({});
  
  // Sidebar state - determines if sidebar is pinned open or auto-collapsing
  const [sidebarPinned, setSidebarPinned] = useState<boolean>(true);
  
  // View state - determines which component to show
  const [currentView, setCurrentView] = useState<'home' | 'login' | 'signup' | 'welcome' | 'landing' | 'protocol' | 'guided' | 'summary' | 'dashboard' | 'agent-chat' | 'settings'>('home');
  // Which Agent Chat mode the 'agent-chat' view is showing: drafting (Agent Mode) or review (Review Response).
  const [agentSessionType, setAgentSessionType] = useState<'drafting' | 'review'>('drafting');

  // BYOK: the user's stored API key status drives the AI hard gate. undefined = not
  // yet checked (don't flash the gate while loading).
  const [apiKeyInfo, setApiKeyInfo] = useState<ApiKeyInfo | null | undefined>(undefined);
  const [freeTier, setFreeTier] = useState<FreeTierInfo | null>(null);
  const [settingsNotice, setSettingsNotice] = useState<string | null>(null);

  // Login modal state
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginModalMode, setLoginModalMode] = useState<'login' | 'signup'>('login');

  // Feedback modal state
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  // "Create New Protocol" → mode chooser (Agent / Guided)
  const [showCreateChooser, setShowCreateChooser] = useState(false);

  // Delete confirmation modal state
  const [deleteConfirmation, setDeleteConfirmation] = useState<{show: boolean, protocol: Protocol | null}>({
    show: false,
    protocol: null
  });

  // Hierarchical procedure selection state
  const [hierarchicalProcedures, setHierarchicalProcedures] = useState<{
    selectedIds: number[];
    selectedProcedures: any[];
    saved: boolean;
  }>({
    selectedIds: [],
    selectedProcedures: [],
    saved: false
  });

  // Mode 2: AI Review Feedback state
  const [aiReviewFeedback, setAiReviewFeedback] = useState<{[key: string]: string}>({});
  const [showReviewFeedback, setShowReviewFeedback] = useState<{[key: string]: boolean}>({});

  // Scaffold generation state for Section 5/6
  const [generatingScaffold, setGeneratingScaffold] = useState<{[key: string]: boolean}>({});

  // Authentication form state
  const [authForm, setAuthForm] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    institution: '',
    department: ''
  });

  // Debounce hook for auto-save performance
  const useDebounce = (callback: Function, delay: number) => {
    const debounceRef = useRef<NodeJS.Timeout | null>(null);
    
    return useCallback((...args: any[]) => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      debounceRef.current = setTimeout(() => {
        callback(...args);
      }, delay);
    }, [callback, delay]);
  };

  // Authentication helper functions
  const saveAuthState = (authData: AuthState) => {
    localStorage.setItem('auth_token', authData.token || '');
    localStorage.setItem('auth_user', JSON.stringify(authData.user));
    setAuthState(authData);
  };

  const clearAuthState = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
    setAuthState({
      user: null,
      token: null,
      isAuthenticated: false,
      needsWelcome: false
    });
  };

  const getAuthHeaders = (): Record<string, string> => {
    const token = localStorage.getItem('auth_token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  // Check for existing session on app load
  useEffect(() => {
    const checkExistingSession = async () => {
      const token = localStorage.getItem('auth_token');
      const userStr = localStorage.getItem('auth_user');
      
      if (token && userStr) {
        try {
          const response = await fetch(`${API_BASE_URL}/api/auth/verify`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          
          if (response.ok) {
            const data = await response.json();
            setAuthState({
              user: data.user,
              token: token,
              isAuthenticated: true,
              needsWelcome: data.needsWelcome
            });
            
            if (data.needsWelcome) {
              setCurrentView('welcome');
            } else {
              setCurrentView('landing');
              loadUserProtocols();
            }
          } else {
            clearAuthState();
          }
        } catch (error) {
          console.error('Session verification failed:', error);
          clearAuthState();
        }
      }
    };

    checkExistingSession();
  }, []);

  // BYOK: load the caller's API key status whenever they become authenticated.
  useEffect(() => {
    if (!authState.isAuthenticated) {
      setApiKeyInfo(undefined);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/user/api-key`, { headers: getAuthHeaders() });
        if (!cancelled && res.ok) {
          const data = await res.json();
          setApiKeyInfo(data.apiKey || null);
          setFreeTier(data.freeTier || null);
        } else if (!cancelled) {
          setApiKeyInfo(null);
        }
      } catch {
        if (!cancelled) setApiKeyInfo(null);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authState.isAuthenticated]);

  // AI is allowed with a usable stored key, or — with no key at all — on the free tier.
  // Admins always fall back to the system key. A stored key that went invalid blocks AI
  // (the server never silently moves a key holder onto the owner's key); removing the key
  // drops the user back to the free tier.
  const aiAllowed =
    (!!apiKeyInfo && apiKeyInfo.status !== 'invalid') ||
    (!apiKeyInfo && !!freeTier?.enabled) ||
    !!authState.user?.is_admin;
  const apiKeyChecked = apiKeyInfo !== undefined;

  // Send the user to Settings with an explanation instead of starting an AI mode.
  const redirectToSettingsForKey = () => {
    setSettingsNotice(
      apiKeyInfo?.status === 'invalid'
        ? (freeTier?.enabled
            ? 'Your API key was rejected by Anthropic. Replace it below, or remove it to go back to the free tier.'
            : 'Your API key was rejected by Anthropic. Replace it below to re-enable AI features.')
        : 'Agent Mode, Guided Mode, and AI assistance run on your own Anthropic API key. Add it below to get started.'
    );
    setCurrentView('settings');
  };

  // Load structured components and set state when switching to Question 4.2
  useEffect(() => {
    if (currentProtocol && currentSection === 4) {
      loadStructuredComponents('4.2');
      
      // Auto-determine Question 4.2 state based on existing answer
      // Only set to final on initial load, not during the building process
      const question42Data = sectionAnswers['4.2'];
      if (question42Data && question42Data.answer && question42Data.answer.trim() && question42Data.answer !== 'test. This didnt fill out.') {
        // Only auto-set to final if we're currently in choosing state (initial load)
        // Don't override building state during active work
        setQuestion42State(prevState => prevState === 'choosing' ? 'final' : prevState);
      } else {
        setQuestion42State('choosing');
      }
    }
  }, [currentProtocol, currentSection]);

  // Authentication functions
  const handleLogin = async () => {
    if (!authForm.email.trim() || !authForm.password.trim()) return;
    
    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: authForm.email.trim(),
          password: authForm.password.trim()
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to login');
      }
      
      const data = await response.json();
      const authData: AuthState = {
        user: data.user,
        token: data.token,
        isAuthenticated: true,
        needsWelcome: data.needsWelcome
      };
      
      saveAuthState(authData);
      setShowLoginModal(false);

      if (data.needsWelcome) {
        setCurrentView('welcome');
      } else {
        setCurrentView('landing');
        loadUserProtocols();
      }
    } catch (error) {
      console.error('Login error:', error);
      alert(error instanceof Error ? error.message : 'Failed to login. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignup = async () => {
    if (!authForm.email.trim() || !authForm.password.trim() || !authForm.name.trim()) return;
    
    if (authForm.password !== authForm.confirmPassword) {
      alert('Passwords do not match');
      return;
    }
    
    if (authForm.password.length < 8) {
      alert('Password must be at least 8 characters');
      return;
    }
    
    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: authForm.email.trim(),
          password: authForm.password.trim(),
          name: authForm.name.trim(),
          institution: authForm.institution.trim() || undefined,
          department: authForm.department.trim() || undefined
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to register');
      }
      
      const data = await response.json();
      const authData: AuthState = {
        user: data.user,
        token: data.token,
        isAuthenticated: true,
        needsWelcome: data.needsWelcome
      };
      
      saveAuthState(authData);
      setShowLoginModal(false);
      setCurrentView('welcome'); // New users always see welcome
    } catch (error) {
      console.error('Signup error:', error);
      alert(error instanceof Error ? error.message : 'Failed to register. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API_BASE_URL}/api/auth/logout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        }
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      clearAuthState();
      setCurrentView('home');
      setShowLoginModal(false);
      setCurrentProtocol(null);
      setProtocols([]);
    }
  };

  const handleWelcomeComplete = async () => {
    try {
      await fetch(`${API_BASE_URL}/api/auth/welcome-seen`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        }
      });
      
      setAuthState(prev => ({ ...prev, needsWelcome: false }));
      setCurrentView('landing');
      loadUserProtocols();
    } catch (error) {
      console.error('Welcome completion error:', error);
      setCurrentView('landing');
      loadUserProtocols();
    }
  };

  // Hierarchical procedure selection handlers
  const handleProcedureSelectionChange = async (selectedIds: number[], selectedProcedures: any[]) => {
    if (!currentProtocol) return;

    setHierarchicalProcedures({
      selectedIds,
      selectedProcedures,
      saved: false
    });

    // Auto-save selections to backend
    try {
      const response = await fetch(`${API_BASE_URL}/api/procedures/select`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({
          protocolId: currentProtocol.id,
          selectedProcedureIds: selectedIds
        })
      });

      if (response.ok) {
        setHierarchicalProcedures(prev => ({ ...prev, saved: true }));
      }
    } catch (error) {
      console.error('Error saving procedure selections:', error);
    }
  };

  const handleGenerateQ44Text = async () => {
    if (!currentProtocol) return;

    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/procedures/generate-text`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({
          protocolId: currentProtocol.id
        })
      });

      if (!response.ok) {
        throw new Error('Failed to generate text');
      }

      const result = await response.json();
      
      // Update the Q4.4 answer with generated text
      setSectionAnswers(prev => ({
        ...prev,
        '4.4': {
          answer: result.generatedText,
          followupData: prev['4.4']?.followupData || []
        }
      }));
      
      // Show success message
      alert('Question 4.4 text generated successfully! You can now review and edit it below.');

    } catch (error) {
      console.error('Error generating Q4.4 text:', error);
      alert('Failed to generate text. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle surgical procedure selection changes
  const handleSurgicalProcedureSelectionChange = async (selections: any[]) => {
    // This is automatically saved by the HierarchicalSurgicalProcedureSelector component
    // Just store in local state for reference if needed
    console.log('Surgical procedure selections updated:', selections);
  };

  // Generate Q4.5 text from selected surgical procedures
  const handleGenerateQ45Text = async () => {
    if (!currentProtocol) return;
    console.log('🔧 DEBUG: Starting Q4.5 text generation for protocol:', currentProtocol.id);
    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/surgical-procedures/generate-text`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({
          protocolId: currentProtocol.id
        })
      });
      
      console.log('🔧 DEBUG: API response status:', response.status, response.statusText);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.log('🔧 DEBUG: API error response:', errorText);
        throw new Error('Failed to generate surgical procedure text');
      }
      
      const result = await response.json();
      console.log('🔧 DEBUG: API success response:', result);
      
      // Check if no procedures were selected
      if (result.generatedText && result.generatedText.includes('No surgical procedures selected')) {
        alert('Please select at least one surgical procedure type (NS, S, or MS) before generating text.');
        return;
      }
      
      // Update the answer for Q4.5 with generated text
      setSectionAnswers(prev => ({
        ...prev,
        '4.5': {
          answer: result.generatedText,
          followupData: prev['4.5']?.followupData || []
        }
      }));
      
      // Generated text is now populated - user can edit and then manually save
      console.log('✅ Generated surgical procedure text populated. User can now edit and save.');
      
    } catch (error) {
      console.error('🔧 DEBUG: Error generating Q4.5 text:', error);
      console.error('🔧 DEBUG: Error details:', error instanceof Error ? error.message : String(error), error instanceof Error ? error.stack : 'No stack trace');
      alert('Failed to generate surgical procedure text. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  

  // Load user's protocols
  const loadUserProtocols = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/user/protocols`, {
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Failed to load protocols');
      
      const protocolsData = await response.json();
      setProtocols(protocolsData);
    } catch (error) {
      console.error('Error loading protocols:', error);
    }
  };
  
  // Create new protocol
  // (createNewProtocol removed — Protocol View is no longer a creation on-ramp; new
  // protocols are created via Agent Chat or Guided Conversation from the chooser.)

  // Delete a protocol
  const deleteProtocol = async (protocolId: number) => {
    if (!authState.isAuthenticated) return;

    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/protocols/${protocolId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete protocol');
      }

      // Remove from local state
      setProtocols(prev => prev.filter(p => p.id !== protocolId));
      setDeleteConfirmation({ show: false, protocol: null });
    } catch (error) {
      console.error('Error deleting protocol:', error);
      alert(error instanceof Error ? error.message : 'Failed to delete protocol. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Start guided conversation for a protocol
  const startGuidedConversation = async (protocolId?: number) => {
    if (!authState.isAuthenticated) return;
    if (!aiAllowed) { redirectToSettingsForKey(); return; }

    setIsLoading(true);
    try {
      let targetProtocolId = protocolId;

      // If no protocol provided, create a new one
      if (!targetProtocolId) {
        const response = await fetch(`${API_BASE_URL}/api/protocols`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeaders()
          },
          body: JSON.stringify({ title: 'New IACUC Protocol' })
        });

        if (!response.ok) throw new Error('Failed to create protocol');

        const newProtocol = await response.json();
        setProtocols(prev => [newProtocol, ...prev]);
        targetProtocolId = newProtocol.id;
        setCurrentProtocol(newProtocol);
      } else {
        // Load existing protocol
        const response = await fetch(`${API_BASE_URL}/api/protocols/${targetProtocolId}`, {
          headers: getAuthHeaders()
        });
        if (!response.ok) throw new Error('Failed to load protocol');
        const protocol = await response.json();
        setCurrentProtocol(protocol);
      }

      // Switch to guided view
      setCurrentView('guided');
    } catch (error) {
      console.error('Error starting guided conversation:', error);
      alert('Failed to start guided conversation. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Start (or re-open) Agent Chat for a protocol. Mirrors startGuidedConversation:
  // no arg → create a new protocol; with arg → load the existing one; then switch view.
  // sessionType selects Agent Mode ('drafting', default) vs Review Response ('review');
  // review always targets an existing protocol (no new-protocol path).
  const startAgentChat = async (protocolId?: number, sessionType: 'drafting' | 'review' = 'drafting') => {
    if (!authState.isAuthenticated) return;
    if (!aiAllowed) { redirectToSettingsForKey(); return; }

    setAgentSessionType(sessionType);
    setIsLoading(true);
    try {
      let targetProtocolId = protocolId;

      if (!targetProtocolId) {
        const response = await fetch(`${API_BASE_URL}/api/protocols`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeaders()
          },
          body: JSON.stringify({ title: 'New IACUC Protocol' })
        });

        if (!response.ok) throw new Error('Failed to create protocol');

        const newProtocol = await response.json();
        setProtocols(prev => [newProtocol, ...prev]);
        setCurrentProtocol(newProtocol);
      } else {
        const response = await fetch(`${API_BASE_URL}/api/protocols/${targetProtocolId}`, {
          headers: getAuthHeaders()
        });
        if (!response.ok) throw new Error('Failed to load protocol');
        const protocol = await response.json();
        setCurrentProtocol(protocol);
      }

      setCurrentView('agent-chat');
    } catch (error) {
      console.error('Error starting agent chat:', error);
      alert('Failed to start Agent Mode. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Select and load protocol
  const selectProtocol = async (protocolId: number) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/protocols/${protocolId}`, {
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Failed to load protocol');
      
      const protocolData = await response.json();
      setCurrentProtocol(protocolData);
      setCurrentView('protocol');
      
      // Load Section 1 by default
      await loadSection(1, protocolData.answers);
      
    } catch (error) {
      console.error('Error loading protocol:', error);
      alert('Failed to load protocol. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Open the standalone Protocol Summary view (the shared "final state" / copy-into-InfoEd
  // endpoint that all three modes funnel into). Loads fresh protocol data so the summary
  // reflects anything just saved in Agent Chat / Guided / Protocol View.
  const openProtocolSummary = async (protocolId?: number) => {
    const id = protocolId ?? currentProtocol?.id;
    if (!id) return;
    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/protocols/${id}`, {
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Failed to load protocol');
      const protocol = await response.json();
      setCurrentProtocol(protocol);
      setCurrentView('summary');
    } catch (error) {
      console.error('Error opening summary:', error);
      alert('Failed to open the protocol summary. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // "Continue" a protocol from the landing page — resume in the mode it was created in.
  // Guided (still in progress) → Guided; everything else → Agent Chat (the flagship
  // workspace, which reads/builds on whatever's already drafted). Protocol View stays a
  // separate per-protocol editor button, not the resume path.
  const continueProtocol = (protocol: Protocol) => {
    if (protocol.entry_mode === 'guided_conversation' && !protocol.guided_complete) {
      startGuidedConversation(protocol.id);
    } else {
      startAgentChat(protocol.id);
    }
  };

  // Load all questions for a section
  const loadSection = async (sectionNumber: number | 'summary', existingAnswers: Answer[] = []) => {
    try {
      // If summary, just set the section and return (no questions to load)
      if (sectionNumber === 'summary') {
        setCurrentSection('summary');
        return;
      }
      
      setCurrentSection(sectionNumber);
      
      // Load all questions
      const questionsResponse = await fetch(`${API_BASE_URL}/api/questions`);
      if (!questionsResponse.ok) throw new Error('Failed to load questions');
      const allQuestions = await questionsResponse.json();
      
      // Filter questions for current section
      const sectionQuestionsList = allQuestions.filter((q: Question) => 
        q.question_number.startsWith(`${sectionNumber}.`)
      );
      setSectionQuestions(sectionQuestionsList);
      
      // Load existing answers for this section
      const answersMap: {[key: string]: {answer: string, followupData: any[]}} = {};
      sectionQuestionsList.forEach((question: Question) => {
        const existingAnswer = existingAnswers.find(
          (a: Answer) => a.question_number === question.question_number
        );
        
        if (existingAnswer?.answer) {
          try {
            const parsedAnswer = JSON.parse(existingAnswer.answer);
            if (parsedAnswer.primary_answer && parsedAnswer.followup_data) {
              answersMap[question.question_number] = {
                answer: parsedAnswer.primary_answer,
                followupData: parsedAnswer.followup_data
              };
            } else {
              answersMap[question.question_number] = {
                answer: existingAnswer.answer,
                followupData: []
              };
            }
          } catch {
            // Not JSON, treat as simple string answer
            answersMap[question.question_number] = {
              answer: existingAnswer.answer,
              followupData: []
            };
          }
        } else {
          answersMap[question.question_number] = {
            answer: '',
            followupData: []
          };
        }
      });
      
      setSectionAnswers(answersMap);
      
    } catch (error) {
      console.error('Error loading section:', error);
    }
  };
  // Validate Phase 2 questions (acknowledgements, conditional followup)
  const validatePhase2Question = (questionNumber: string, questionData: any): string[] => {
    const errors: string[] = [];

    // Q5.10: Must check required veterinary authority
    if (questionNumber === '5.10' && questionData.answer === 'Yes') {
      if (!questionData.acknowledgements?.includes('vet_authority')) {
        errors.push('You must acknowledge the veterinary authority statement');
      }
    }

    // Q5.11: If CFA mentioned, must provide justification
    if (questionNumber === '5.11' && questionData.answer === 'Yes') {
      const hasCFA = /complete freund|CFA/i.test(questionData.followupData[0] || '');
      if (hasCFA && (!questionData.cfaJustification || questionData.cfaJustification.length < 50)) {
        errors.push('Complete Freund\'s Adjuvant requires detailed justification (minimum 50 characters)');
      }
    }

    return errors;
  };

  // Save individual question answer
  const saveQuestionAnswer = async (questionNumber: string, overrideData?: {answer: string, followupData: any[], acknowledgements?: string[], cfaJustification?: string}) => {
    if (!currentProtocol || (!sectionAnswers[questionNumber] && !overrideData)) return;

    // Validate Phase 2 questions before saving
    const questionData = overrideData || sectionAnswers[questionNumber];
    const validationErrors = validatePhase2Question(questionNumber, questionData);
    if (validationErrors.length > 0) {
      alert('Validation Error:\n\n' + validationErrors.join('\n\n'));
      return;
    }
    
    setIsSaving(prev => ({...prev, [questionNumber]: true}));
    try {
      const questionData = overrideData || sectionAnswers[questionNumber];
      const question = sectionQuestions.find(q => q.question_number === questionNumber);
      
      // Prepare answer data - use JSON format if follow-up data exists
      // For structured mode 4.2, use AI suggestion as the answer
      const currentAiSuggestion = aiSuggestions[questionNumber];
      let answerData = (questionNumber === '4.2' && question42Mode === 'structured' && currentAiSuggestion) 
        ? currentAiSuggestion.trim() 
        : questionData.answer.trim();
      const hasFollowupConfig = question?.example_answers?.followup_config;
      const showFollowup = hasFollowupConfig && questionData.answer === hasFollowupConfig.show_on;
      
      
      // Special handling for Q4.4, Q4.5, Q4.6, and Q4.7 which store data in followupData without followup_config
      if ((questionNumber === '4.4' || questionNumber === '4.5' || questionNumber === '4.6' || questionNumber === '4.7') && questionData.followupData.length > 0) {
        answerData = JSON.stringify({
          primary_answer: questionData.answer.trim(),
          followup_data: questionData.followupData
        });
      } else if (showFollowup && questionData.followupData.some((item: any) =>
        typeof item === 'string' ? item.trim() : Object.values(item).some((val: any) => val && val.trim && val.trim())
      )) {
        // Filter out empty entries for cleaner database storage
        const cleanedData = questionData.followupData.filter((item: any) => {
          if (typeof item === 'string') {
            return item.trim();
          } else {
            return Object.values(item).some((val: any) => val && val.trim && val.trim());
          }
        });

        const jsonData: any = {
          primary_answer: questionData.answer.trim(),
          followup_data: cleanedData
        };

        // Add acknowledgements if present (Phase 2 questions: 5.4, 5.10)
        if (questionData.acknowledgements && questionData.acknowledgements.length > 0) {
          jsonData.acknowledgements = questionData.acknowledgements;
        }

        // Add CFA justification if present (Q5.11)
        if (questionData.cfaJustification) {
          jsonData.cfa_justification = questionData.cfaJustification;
        }

        answerData = JSON.stringify(jsonData);
      }
      
      const response = await fetch(`${API_BASE_URL}/api/protocols/${currentProtocol.id}/answers`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({
          questionNumber: questionNumber,
          answer: answerData
        })
      });
      
      if (!response.ok) throw new Error('Failed to save answer');
      
      // Refresh protocol data to get updated answers
      const protocolResponse = await fetch(`${API_BASE_URL}/api/protocols/${currentProtocol.id}`, {
        headers: getAuthHeaders()
      });
      if (protocolResponse.ok) {
        const updatedProtocol = await protocolResponse.json();
        setCurrentProtocol(updatedProtocol);
        
        // Also update the protocols list for landing page sync
        setProtocols(prev => prev.map(p => 
          p.id === updatedProtocol.id ? updatedProtocol : p
        ));
      }
      
      // Show success message for surgical description saves
      if (questionNumber === '4.5') {
        alert('✅ Surgical description saved successfully!');
      }
      
    } catch (error) {
      console.error('Error saving answer:', error);
      alert('Failed to save answer. Please try again.');
    } finally {
      setIsSaving(prev => ({...prev, [questionNumber]: false}));
    }
  };

  // Generate AI scaffold for Section 5/6 questions
  const generateScaffold = async (questionNumber: string) => {
    if (!currentProtocol) return;

    setGeneratingScaffold(prev => ({...prev, [questionNumber]: true}));

    try {
      const question = sectionQuestions.find(q => q.question_number === questionNumber);
      const scaffoldConfig = question?.example_answers?.scaffold_config;

      if (!scaffoldConfig || scaffoldConfig.type !== 'ai_generated') {
        throw new Error('Scaffold generation not supported for this question');
      }

      // Collect cross-referenced answers
      const crossRefs = scaffoldConfig?.cross_references || [];
      const crossRefData: {[key: string]: string} = {};

      for (const refNum of crossRefs) {
        if (sectionAnswers[refNum]?.answer) {
          crossRefData[refNum] = sectionAnswers[refNum].answer;
        }
      }

      // Get current textarea content (may be empty)
      const currentContent = sectionAnswers[questionNumber]?.answer || '';

      // Call backend
      const response = await fetch(`${API_BASE_URL}/api/ai-scaffold`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({
          questionNumber,
          protocolId: currentProtocol.id,
          crossReferences: crossRefData,
          currentContent: currentContent
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Scaffold generation failed');
      }

      const data = await response.json();

      // Special handling for Q5.5 compound scanning (JSON response)
      if (questionNumber === '5.5') {
        try {
          // AI should return JSON array of compounds
          const compounds = JSON.parse(data.scaffold);

          if (Array.isArray(compounds)) {
            // Convert AI format to our compound format
            const formattedCompounds = compounds.map((c: any) => ({
              compound_name: c.compound || '',
              justification: c.context ? `Detected: ${c.context}` : ''
            }));

            // Update followupData with detected compounds
            setSectionAnswers(prev => {
              const currentData = prev[questionNumber] || { answer: 'Yes', followupData: [] };
              const newData = [...currentData.followupData];
              if (!newData[0]) newData[0] = { compounds: [] };

              // Merge detected compounds with existing ones (avoid duplicates)
              const existingCompounds = newData[0].compounds || [];
              const existingNames = existingCompounds.map((c: any) => c.compound_name.toLowerCase());

              formattedCompounds.forEach((newCompound: any) => {
                if (!existingNames.includes(newCompound.compound_name.toLowerCase())) {
                  existingCompounds.push(newCompound);
                }
              });

              newData[0].compounds = existingCompounds;

              return {
                ...prev,
                [questionNumber]: {
                  ...currentData,
                  followupData: newData
                }
              };
            });

            alert(`Found ${formattedCompounds.length} compound(s) in your protocol. Review and edit the justifications as needed.`);
          } else {
            throw new Error('Expected JSON array of compounds');
          }
        } catch (parseError) {
          console.error('Failed to parse compound scanning result:', parseError);
          alert('AI response format error. Please try again.');
        }
      } else {
        // Standard scaffold generation for other questions (updates answer field)
        updateQuestionAnswer(questionNumber, data.scaffold);
      }

    } catch (error) {
      console.error('Scaffold error:', error);
      alert(`Failed to generate scaffold: ${error instanceof Error ? error.message : 'Unknown error'}. Please try again.`);
    } finally {
      setGeneratingScaffold(prev => ({...prev, [questionNumber]: false}));
    }
  };

  // Update individual question answer (optimized with useCallback)
  const updateQuestionAnswer = useCallback((questionNumber: string, answer: string) => {
    setSectionAnswers(prev => {
      const currentQuestion = sectionQuestions.find(q => q.question_number === questionNumber);
      const currentData = prev[questionNumber] || { answer: '', followupData: [] };
      
      // Check if this question has follow-up configuration and user answered "Yes"
      let newFollowupData = currentData.followupData;
      if (currentQuestion?.example_answers?.followup_config && 
          answer === currentQuestion.example_answers.followup_config.show_on &&
          currentData.followupData.length === 0) {
        // Initialize follow-up data based on type
        if (currentQuestion.example_answers.followup_config.type === 'list') {
          // For list type (like question 1.2), start with one empty string
          newFollowupData = [''];
        } else if (currentQuestion.example_answers.followup_config.type === 'structured_list') {
          // For fields type (like question 1.3), start with one empty object with all required fields
          const newEntry: any = {};
          currentQuestion.example_answers.followup_config.fields.forEach((field: any) => {
            newEntry[field.name] = '';
          });
          newFollowupData = [newEntry];
        } else if (currentQuestion.example_answers.followup_config.type === 'chained_conditional') {
          // For chained conditional type (like Q5.5), start with one object containing compounds array
          newFollowupData = [{ compounds: [] }];
        }
      } else if (currentQuestion?.example_answers?.followup_config && 
                 answer !== currentQuestion.example_answers.followup_config.show_on) {
        // If user changed from "Yes" to something else, clear follow-up data
        newFollowupData = [];
      }
      
      return {
        ...prev,
        [questionNumber]: {
          ...currentData,
          answer: answer,
          followupData: newFollowupData
        }
      };
    });
  }, [sectionQuestions]);
  
  // Update individual question followup data (optimized with useCallback)
  const updateQuestionFollowupData = useCallback((questionNumber: string, followupData: any[]) => {
    setSectionAnswers(prev => ({
      ...prev,
      [questionNumber]: {
        ...prev[questionNumber],
        followupData: followupData
      }
    }));
  }, []);

  // Update question acknowledgements (for Q5.4, Q5.10)
  const updateQuestionAcknowledgements = useCallback((questionNumber: string, acknowledgements: string[]) => {
    setSectionAnswers(prev => ({
      ...prev,
      [questionNumber]: {
        ...prev[questionNumber],
        acknowledgements: acknowledgements
      }
    }));
  }, []);

  // Handle acknowledgement checkbox change with mutually exclusive logic
  const handleAcknowledgementChange = useCallback((
    questionNumber: string,
    ackId: string,
    checked: boolean,
    mutuallyExclusiveWith: string | null
  ) => {
    setSectionAnswers(prev => {
      const current = prev[questionNumber] || { answer: '', followupData: [] };
      let newAcknowledgements = [...(current.acknowledgements || [])];

      if (checked) {
        // Add this acknowledgement
        if (!newAcknowledgements.includes(ackId)) {
          newAcknowledgements.push(ackId);
        }
        // Remove mutually exclusive one if present
        if (mutuallyExclusiveWith && newAcknowledgements.includes(mutuallyExclusiveWith)) {
          newAcknowledgements = newAcknowledgements.filter(id => id !== mutuallyExclusiveWith);
        }
      } else {
        // Remove this acknowledgement
        newAcknowledgements = newAcknowledgements.filter(id => id !== ackId);
      }

      return {
        ...prev,
        [questionNumber]: {
          ...current,
          acknowledgements: newAcknowledgements
        }
      };
    });
  }, []);

  // Update CFA justification for Q5.11
  const updateCfaJustification = useCallback((questionNumber: string, justification: string) => {
    setSectionAnswers(prev => ({
      ...prev,
      [questionNumber]: {
        ...prev[questionNumber],
        cfaJustification: justification
      }
    }));
  }, []);

  // Check if conditional followup should be shown (Q5.11 CFA detection)
  const shouldShowConditionalFollowup = useCallback((text: string, conditionalConfig: any): boolean => {
    if (!text || !conditionalConfig?.trigger_text) return false;
    const lowerText = text.toLowerCase();
    return conditionalConfig.trigger_text.some((trigger: string) =>
      lowerText.includes(trigger.toLowerCase())
    );
  }, []);
  
  // Structured components management
  const loadStructuredComponents = async (questionNumber: string) => {
    if (!currentProtocol) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/protocols/${currentProtocol.id}/components/${questionNumber}`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const components = await response.json();
        setStructuredComponents(prev => ({
          ...prev,
          [questionNumber]: components
        }));
      }
    } catch (error) {
      console.error('Error loading structured components:', error);
    }
  };

  const saveStructuredComponents = async (questionNumber: string, components: {[key: string]: string}) => {
    if (!currentProtocol) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/protocols/${currentProtocol.id}/components`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({ questionNumber, components })
      });
      
      if (response.ok) {
        setStructuredComponents(prev => ({
          ...prev,
          [questionNumber]: components
        }));
      }
    } catch (error) {
      console.error('Error saving structured components:', error);
    }
  };

  const clearStructuredComponents = async (questionNumber: string) => {
    if (!currentProtocol) return;
    
    try {
      await fetch(`${API_BASE_URL}/api/protocols/${currentProtocol.id}/components/${questionNumber}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      
      setStructuredComponents(prev => ({
        ...prev,
        [questionNumber]: {}
      }));
    } catch (error) {
      console.error('Error clearing structured components:', error);
    }
  };

  // Debounced save function for structured components
  const debouncedSaveStructuredComponents = useDebounce(saveStructuredComponents, 500);
  
  // Debounced function for regular text inputs (shorter delay for better responsiveness)
  const debouncedUpdateQuestionAnswer = useDebounce(updateQuestionAnswer, 200);

  const updateStructuredComponent = useCallback((questionNumber: string, componentType: string, content: string) => {
    const currentComponents = structuredComponents[questionNumber] || {};
    const updatedComponents = {
      ...currentComponents,
      [componentType]: content
    };
    
    // Update state immediately for UI responsiveness
    setStructuredComponents(prev => ({
      ...prev,
      [questionNumber]: updatedComponents
    }));
    
    // Debounced auto-save (500ms delay)
    debouncedSaveStructuredComponents(questionNumber, updatedComponents);
  }, [structuredComponents, debouncedSaveStructuredComponents]);
  
  // Determine AI interaction mode for a question
  const getAiMode = (questionNumber: string): 'rewrite' | 'review' => {
    // Mode 2 (Review → Optional Rewrite) for complex questions
    const reviewModeQuestions = ['4.2', '4.5'];
    return reviewModeQuestions.includes(questionNumber) ? 'review' : 'rewrite';
  };

  // Handle applying AI feedback (Mode 2 Step 2)
  const handleApplyFeedback = async (questionNumber: string) => {
    const originalText = sectionAnswers[questionNumber]?.answer || '';
    const feedback = aiReviewFeedback[questionNumber] || '';

    if (!originalText.trim() || !feedback.trim()) {
      alert('Both original text and feedback are required');
      return;
    }

    setIsSaving(prev => ({...prev, [questionNumber]: true}));
    try {
      const response = await fetch(`${API_BASE_URL}/api/ai-apply-feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({
          originalText,
          feedback,
          questionNumber
        })
      });

      if (!response.ok) throw new Error('Failed to apply feedback');

      const data = await response.json();

      // Set the rewritten text as AI suggestion (Mode 1 style)
      setAiSuggestions(prev => ({
        ...prev,
        [questionNumber]: data.rewrittenText
      }));

      // Hide the review feedback box
      setShowReviewFeedback(prev => ({
        ...prev,
        [questionNumber]: false
      }));

    } catch (error) {
      console.error('Error applying feedback:', error);
      alert('Failed to apply feedback. Please try again.');
    } finally {
      setIsSaving(prev => ({...prev, [questionNumber]: false}));
    }
  };

  // Get AI assistance for individual question
  const getQuestionAiAssistance = async (questionNumber: string) => {
    const questionData = sectionAnswers[questionNumber];
    const question = sectionQuestions.find(q => q.question_number === questionNumber);
    
    // For structured mode 4.2, allow AI assistance even if main answer is empty
    const hasContent = questionNumber === '4.2' && question42Mode === 'structured' ? true : questionData?.answer.trim();
    if (!hasContent || !question) return;
    
    setIsSaving(prev => ({...prev, [questionNumber]: true}));
    try {
      let prompt = `You are helping complete an IACUC (Institutional Animal Care and Use Committee) protocol form. The user is working on Question ${questionNumber}: "${question.question_text}"\n\n`;
      
      // For Question 4.2, handle mode and structured components
      if (questionNumber === '4.2') {
        if (question42Mode === 'free_text') {
          prompt += `User's answer: "${questionData.answer}"\n\nMode: FREE TEXT MODE\n\n`;
        } else {
          // Get structured components for this question
          const components = structuredComponents[questionNumber] || {};
          const existingAnswer = questionData.answer || '';
          
          prompt += `Mode: STRUCTURED MODE\n\nUser provided components:\n\n`;
          
          if (components.hypothesis) prompt += `HYPOTHESIS: ${components.hypothesis}\n\n`;
          if (components.groups) prompt += `EXPERIMENTAL GROUPS: ${components.groups}\n\n`;
          if (components.timeline) prompt += `TIMELINE & PROCEDURES: ${components.timeline}\n\n`;
          if (components.treatment) prompt += `TREATMENT DETAILS: ${components.treatment}\n\n`;
          
          if (existingAnswer && existingAnswer.trim()) {
            prompt += `EXISTING EXPERIMENTAL DESIGN:\n${existingAnswer}\n\n`;
          }
        }
      } else if (questionNumber === '4.3') {
        // Handle Question 4.3 with numerical inputs
        prompt += `User's answer: "${questionData.answer}"\n\n`;
        
        // Add numerical inputs
        const miceCount = questionData.followupData[0] || '0';
        const ratsCount = questionData.followupData[1] || '0';
        const otherSpecies = questionData.followupData[2] || 'No';
        
        prompt += `Mice: ${miceCount}\n`;
        prompt += `Rats: ${ratsCount}\n`;
        prompt += `Other Species: ${otherSpecies}\n\n`;
      } else {
        prompt += `User's answer: "${questionData.answer}"\n\n`;
      }
      
      if (question.requirements) {
        prompt += `Requirements: ${question.requirements}\n\n`;
      }
      
      if (question.example_answers && Array.isArray(question.example_answers)) {
        prompt += `Example answers for reference:\n${question.example_answers.map((ex: any) => `${ex.title}: ${ex.content}`).join('\n\n')}\n\n`;
      }
      
      if (questionNumber === '4.2' && question42Mode === 'free_text') {
        prompt += 'Provide specific, actionable feedback on completeness and suggest improvements. Focus on IACUC requirements:';
      } else {
        prompt += 'Provide only the improved answer without preamble, commentary, or explanations:';
      }
      
      const response = await fetch(`${API_BASE_URL}/api/chat`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({ 
          message: prompt,
          protocolId: currentProtocol?.id,
          questionNumber: questionNumber,
          mode: questionNumber === '4.2' ? question42Mode : undefined
        })
      });
      
      if (!response.ok) throw new Error('Failed to get AI assistance');

      const data = await response.json();

      // Determine mode and route response accordingly
      const mode = getAiMode(questionNumber);

      if (mode === 'review') {
        // Mode 2: Show editable review feedback
        setAiReviewFeedback(prev => ({
          ...prev,
          [questionNumber]: data.botResponse
        }));
        setShowReviewFeedback(prev => ({
          ...prev,
          [questionNumber]: true
        }));
      } else {
        // Mode 1: Show AI suggestion as direct rewrite
        setAiSuggestions(prev => ({
          ...prev,
          [questionNumber]: data.botResponse
        }));
      }

    } catch (error) {
      console.error('Error getting AI assistance:', error);
      alert('Failed to get AI assistance. Please try again.');
    } finally {
      setIsSaving(prev => ({...prev, [questionNumber]: false}));
    }
  };
  
  

  // Navigate back to landing page
  const goToLanding = () => {
    setCurrentView('landing');
    setCurrentProtocol(null);
    setSectionQuestions([]);
    setSectionAnswers({});
    setAiSuggestions({});
    // Refresh protocol list to show updated titles and data
    loadUserProtocols();
  };

  // Render landing page
  // Public home page - visible to all visitors
  const renderHomePage = () => {
    return (
      <div className="home-page">
        {/* Hero Section */}
        <section className="hero-section">
          <div className="hero-content">
            <h1 className="hero-title">AI-Assisted Protocol Completion</h1>
            <p className="hero-subtitle">
              Streamline regulatory form completion with intelligent guidance and validation.
            </p>
            <div className="hero-actions">
              <button
                className="primary-button large"
                onClick={() => {
                  setLoginModalMode('login');
                  setShowLoginModal(true);
                }}
              >
                Sign In
              </button>
              <button
                className="secondary-button large"
                onClick={() => {
                  setLoginModalMode('signup');
                  setShowLoginModal(true);
                }}
              >
                Create Account
              </button>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="features-section">
          <h2 className="features-title">How It Works</h2>
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">1</div>
              <h3>Structured Sections</h3>
              <p>Navigate through organized protocol sections with clear guidance at each step.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">2</div>
              <h3>AI Assistance</h3>
              <p>Get intelligent suggestions and help with complex questions and regulatory language.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">3</div>
              <h3>Auto-Save</h3>
              <p>Your progress is saved automatically. Return anytime to continue where you left off.</p>
            </div>
          </div>
        </section>

        {/* Notice Section */}
        <section className="notice-section">
          <div className="notice-card">
            <h3>Preview Version</h3>
            <p>
              This system is currently in <strong>preview/demonstration mode</strong>.
              It showcases AI-assisted form completion capabilities and is provided for
              evaluation and testing purposes.
            </p>
            <ul>
              <li>AI suggestions are for guidance only and should be reviewed carefully</li>
              <li>Data entered during this preview period may be cleared periodically</li>
              <li>For official submissions, please follow your institution's established processes</li>
            </ul>
            <p className="notice-footer">
              Questions or feedback? Contact us at <a href="mailto:contact@tophertech.org">contact@tophertech.org</a>
            </p>
          </div>
        </section>
      </div>
    );
  };

  // Login/Signup Modal
  const renderLoginModal = () => {
    if (!showLoginModal) return null;

    const handleOverlayClick = () => {
      // Don't close if user is selecting text (prevents accidental close when highlighting password)
      const selection = window.getSelection();
      if (selection && selection.toString().length > 0) {
        return;
      }
      setShowLoginModal(false);
    };

    return (
      <div className="modal-overlay" onClick={handleOverlayClick}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <button className="modal-close" onClick={() => setShowLoginModal(false)}>×</button>

          {loginModalMode === 'login' ? (
            <>
              <h2>Sign In</h2>
              <div className="form-group">
                <label>Email Address *</label>
                <input
                  type="email"
                  value={authForm.email}
                  onChange={(e) => setAuthForm(prev => ({ ...prev, email: e.target.value }))}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && authForm.email.trim() && authForm.password.trim()) {
                      handleLogin();
                    }
                  }}
                  placeholder="your.email@institution.edu"
                  required
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label>Password *</label>
                <input
                  type="password"
                  value={authForm.password}
                  onChange={(e) => setAuthForm(prev => ({ ...prev, password: e.target.value }))}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && authForm.email.trim() && authForm.password.trim()) {
                      handleLogin();
                    }
                  }}
                  placeholder="Your password"
                  required
                />
              </div>
              <button
                className="primary-button full-width"
                onClick={handleLogin}
                disabled={isLoading || !authForm.email.trim() || !authForm.password.trim()}
              >
                {isLoading ? 'Signing in...' : 'Sign In'}
              </button>
              <p className="auth-switch">
                Don't have an account? <button className="link-button" onClick={() => setLoginModalMode('signup')}>Sign Up</button>
              </p>
            </>
          ) : (
            <>
              <h2>Create Account</h2>
              <div className="form-group">
                <label>Email Address *</label>
                <input
                  type="email"
                  value={authForm.email}
                  onChange={(e) => setAuthForm(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="your.email@institution.edu"
                  required
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label>Password *</label>
                <input
                  type="password"
                  value={authForm.password}
                  onChange={(e) => setAuthForm(prev => ({ ...prev, password: e.target.value }))}
                  placeholder="At least 8 characters"
                  required
                />
              </div>
              <div className="form-group">
                <label>Confirm Password *</label>
                <input
                  type="password"
                  value={authForm.confirmPassword}
                  onChange={(e) => setAuthForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                  placeholder="Confirm your password"
                  required
                />
              </div>
              <div className="form-group">
                <label>Full Name *</label>
                <input
                  type="text"
                  value={authForm.name}
                  onChange={(e) => setAuthForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Dr. Jane Smith"
                  required
                />
              </div>
              <div className="form-group">
                <label>Institution</label>
                <input
                  type="text"
                  value={authForm.institution}
                  onChange={(e) => setAuthForm(prev => ({ ...prev, institution: e.target.value }))}
                  placeholder="State University"
                />
              </div>
              <div className="form-group">
                <label>Department</label>
                <input
                  type="text"
                  value={authForm.department}
                  onChange={(e) => setAuthForm(prev => ({ ...prev, department: e.target.value }))}
                  placeholder="Biology"
                />
              </div>
              <button
                className="primary-button full-width"
                onClick={handleSignup}
                disabled={isLoading || !authForm.email.trim() || !authForm.password.trim() || !authForm.name.trim()}
              >
                {isLoading ? 'Creating Account...' : 'Create Account'}
              </button>
              <p className="auth-switch">
                Already have an account? <button className="link-button" onClick={() => setLoginModalMode('login')}>Sign In</button>
              </p>
            </>
          )}
        </div>
      </div>
    );
  };

  // "Create New Protocol" → choose how to start (Agent Chat or Guided Conversation).
  // Protocol View is intentionally NOT a creation on-ramp — it's the per-protocol editor.
  const renderCreateChooserModal = () => {
    if (!showCreateChooser) return null;
    return (
      <div className="modal-overlay" onClick={() => setShowCreateChooser(false)}>
        <div className="modal-content create-chooser-modal" onClick={(e) => e.stopPropagation()}>
          <button className="modal-close" onClick={() => setShowCreateChooser(false)}>×</button>
          <h2>Create New Protocol</h2>
          <p className="create-chooser-subtitle">
            How would you like to start? You can fine-tune individual questions in Protocol View any time afterward.
          </p>
          <div className="create-chooser-options">
            <button
              className="create-chooser-card recommended"
              onClick={() => { setShowCreateChooser(false); startAgentChat(); }}
              disabled={isLoading}
            >
              <span className="create-chooser-badge">Recommended</span>
              <span className="create-chooser-title">🤖 Agent Mode</span>
              <span className="create-chooser-desc">
                Chat freely with an AI assistant that drafts and saves your narrative sections as you go.
              </span>
            </button>
            <button
              className="create-chooser-card"
              onClick={() => { setShowCreateChooser(false); startGuidedConversation(); }}
              disabled={isLoading}
            >
              <span className="create-chooser-title">🧭 Guided Mode</span>
              <span className="create-chooser-desc">
                A step-by-step interview that walks you through each narrative section in order.
              </span>
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Delete confirmation modal
  const renderDeleteConfirmationModal = () => {
    if (!deleteConfirmation.show || !deleteConfirmation.protocol) return null;

    return (
      <div className="modal-overlay" onClick={() => setDeleteConfirmation({ show: false, protocol: null })}>
        <div className="modal-content delete-modal" onClick={(e) => e.stopPropagation()}>
          <button className="modal-close" onClick={() => setDeleteConfirmation({ show: false, protocol: null })}>×</button>

          <div className="delete-warning-icon">⚠️</div>
          <h2>Delete Protocol?</h2>
          <p className="delete-warning-text">
            Are you sure you want to delete <strong>"{deleteConfirmation.protocol.title}"</strong>?
          </p>
          <p className="delete-warning-detail">
            This will permanently remove the protocol and all associated data including answers, procedures, and any other saved information. This action cannot be undone.
          </p>

          <div className="delete-modal-actions">
            <button
              className="secondary-button"
              onClick={() => setDeleteConfirmation({ show: false, protocol: null })}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              className="danger-button"
              onClick={() => deleteProtocol(deleteConfirmation.protocol!.id)}
              disabled={isLoading}
            >
              {isLoading ? 'Deleting...' : 'Delete Protocol'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderLandingPage = () => {
    // Sort all protocols by updated_at (most recent first)
    const sortedProtocols = [...protocols].sort((a, b) => {
      const dateA = a.updated_at ? new Date(a.updated_at).getTime() : 0;
      const dateB = b.updated_at ? new Date(b.updated_at).getTime() : 0;
      return dateB - dateA;
    });

    return (
      <div className="landing-page">
        <div className="landing-header">
          <h2>Welcome, {authState.user?.email}</h2>
          <button
            className="secondary-button"
            onClick={() => { setSettingsNotice(null); setCurrentView('settings'); }}
            title="Account, API key, model, and usage"
          >
            Settings
          </button>
          <button
            className="logout-button"
            onClick={handleLogout}
          >
            Logout
          </button>
          <div className="new-protocol-buttons">
            <button
              className="primary-button"
              onClick={() => setShowCreateChooser(true)}
              disabled={isLoading}
            >
              {isLoading ? 'Creating...' : '+ Create New Protocol'}
            </button>
          </div>
        </div>

        {apiKeyChecked && !aiAllowed && (
          <div className="api-key-banner">
            <span>
              {apiKeyInfo?.status === 'invalid'
                ? 'Your API key was rejected by Anthropic — AI features are paused until you replace it.'
                : 'AI features run on your own Anthropic API key. Add it once in Settings to enable Agent Mode, Guided Mode, and AI assistance.'}
            </span>
            <button className="primary-button" onClick={redirectToSettingsForKey}>
              Open Settings
            </button>
          </div>
        )}

        <div className="protocol-sections">
          {/* Left Panel: All Protocols */}
          <div className="protocol-section">
            <h3>Your Protocols ({sortedProtocols.length})</h3>
            {sortedProtocols.length > 0 ? (
              <div className="protocol-list">
                {sortedProtocols.map(protocol => {
                  const isCompleted = protocol.status === 'completed';
                  const isGuidedMode = protocol.entry_mode === 'guided_conversation' && !protocol.guided_complete;

                  return (
                    <div key={protocol.id} className={`protocol-card ${isCompleted ? 'completed' : ''}`}>
                      <div className="protocol-info">
                        <h4>{protocol.title}</h4>
                        {isCompleted ? (
                          <p>Completed: {protocol.updated_at ? new Date(protocol.updated_at).toLocaleDateString() : 'Unknown date'}</p>
                        ) : (
                          <>
                            <p>Current: Question {protocol.current_question}</p>
                            <p>Updated: {protocol.updated_at ? new Date(protocol.updated_at).toLocaleDateString() : 'Just created'}</p>
                          </>
                        )}
                        {isGuidedMode && (
                          <span className="mode-badge guided">Guided Mode</span>
                        )}
                        {isCompleted && (
                          <span className="mode-badge completed">Completed</span>
                        )}
                      </div>
                      <div className="protocol-actions">
                        {isCompleted ? (
                          <button
                            className="primary-button"
                            onClick={() => openProtocolSummary(protocol.id)}
                            disabled={isLoading}
                            title="View the protocol summary and download/copy for InfoEd"
                          >
                            Summary
                          </button>
                        ) : (
                          <button
                            className="primary-button"
                            onClick={() => continueProtocol(protocol)}
                            disabled={isLoading}
                            title={isGuidedMode ? 'Resume in Guided Mode' : 'Resume in Agent Mode'}
                          >
                            Continue
                          </button>
                        )}
                        <button
                          className="secondary-button"
                          onClick={() => selectProtocol(protocol.id)}
                          disabled={isLoading}
                          title="Open Protocol View to edit individual questions and structured fields"
                        >
                          Protocol View
                        </button>
                        <button
                          className="secondary-button"
                          onClick={() => startAgentChat(protocol.id, 'review')}
                          disabled={isLoading}
                          title="Paste the comments your IACUC returned and revise this protocol in response"
                        >
                          Respond to Review
                        </button>
                        {!isCompleted && (
                          <button
                            className="secondary-button"
                            onClick={() => openProtocolSummary(protocol.id)}
                            disabled={isLoading}
                            title="View the protocol summary and download/copy for InfoEd"
                          >
                            Summary
                          </button>
                        )}
                        <button
                          className="delete-button"
                          onClick={() => setDeleteConfirmation({ show: true, protocol })}
                          disabled={isLoading}
                          title="Delete protocol"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="empty-state">No protocols yet. Start your first protocol above!</p>
            )}
          </div>

          {/* Right Panel: User Guide & Settings */}
          <div className="guide-section">
            <h3>User Guide</h3>
            <div className="guide-content">
              <p>
                The goal of this site is to provide AI assistance in completing the IACUC form. Most questions
                on the form are simple data entry and don't benefit meaningfully from AI, so this tool focuses
                primarily on the narrative sections (Experimental Design, Adverse Effects, and so on).
              </p>

              <h4>Important Points</h4>
              <ol className="guide-note-list">
                <li>
                  The AI needs context to build good answers. Starting with your experimental design is advised,
                  before moving to other narrative sections — Guided Mode takes this path explicitly.
                </li>
                <li>
                  While the AI has guidance on each question, its answer is not guaranteed to be complete or
                  accurate. It can generate variable responses across prompts, and may miss things or write
                  excessively.
                </li>
                <li>
                  View the AI as an overall editor of your writing, not a validator of experimental or
                  methodological detail. It can help get you started and give a first editorial pass; it cannot
                  guarantee correct answers on specific details or protocols.
                </li>
              </ol>

              <h4>Start a New Protocol</h4>
              <p>
                Click <strong>+ Create New Protocol</strong> and choose the mode you prefer — Agent or Guided.
              </p>

              <h4>Working with a Protocol</h4>
              <ol>
                <li>
                  <strong>Agent Mode:</strong> Chat directly with an AI agent as it leads you through the narrative
                  sections, saving your drafts as you go. A free-form conversation.
                </li>
                <li>
                  <strong>Guided Mode:</strong> Starts with experimental design and builds progressively through
                  the key sections that benefit from AI assistance — a tighter, question-by-question flow.
                </li>
                <li>
                  <strong>Protocol View:</strong> An overall view of the protocol, including sections that don't
                  benefit from AI. Some questions offer "Get AI Assistance"; others mirror the static form. Useful
                  for reviewing and editing individual answers, though completing the non-AI sections here has
                  little current benefit.
                </li>
                <li>
                  <strong>Summary:</strong> A single overall view of the protocol. Download the whole
                  protocol to a file, or copy individual sections to paste into the official form.
                </li>
                <li>
                  <strong>Review Response:</strong> After you submit, paste the comments your IACUC returns.
                  The agent sorts them into ones it can help revise here and ones you handle in InfoEd,
                  updates the affected narrative sections, and helps draft your response-to-reviewers letter.
                  Open it with <strong>Respond to Review</strong> on any protocol.
                </li>
              </ol>

              <p className="guide-recommendation">
                <strong>Recommendation:</strong> Use Agent Mode for an interactive conversation, or Guided Mode for
                a more focused, step-by-step pass.
              </p>

              <div className="settings-placeholder">
                <h4>Your API Key & Model</h4>
                <p>
                  AI features run on your own Anthropic API key — add it once in{' '}
                  <button className="link-button" onClick={() => { setSettingsNotice(null); setCurrentView('settings'); }}>Settings</button>,
                  where you can also choose your model (Haiku, Sonnet, or Opus) and see your usage. Anthropic's
                  terms indicate API data is not used to train future models, but please do your own research:{' '}
                  <a href="https://privacy.claude.com/en/articles/7996885-how-do-you-use-personal-data-in-model-training" target="_blank" rel="noopener noreferrer">Anthropic Privacy Policy</a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="App">
      {/* Show header only on non-home pages */}
      {currentView !== 'home' && (
        <header className="App-header">
          <h1>AI-Assisted Protocol Completion</h1>
        </header>
      )}

      {/* Login/Signup Modal - renders on top of home page */}
      {renderLoginModal()}

      {/* Delete confirmation modal */}
      {renderDeleteConfirmationModal()}

      {/* Create-new-protocol mode chooser */}
      {renderCreateChooserModal()}

      <main className="main-content">
        {currentView === 'home' ? (
          renderHomePage()
        ) : currentView === 'login' ? (
          <div className="login-section">
            <h2>Sign In</h2>
            <div className="form-group">
              <label>Email Address *</label>
              <input
                type="email"
                value={authForm.email}
                onChange={(e) => setAuthForm(prev => ({ ...prev, email: e.target.value }))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && authForm.email.trim() && authForm.password.trim()) {
                    handleLogin();
                  }
                }}
                placeholder="your.email@institution.edu"
                required
              />
            </div>
            <div className="form-group">
              <label>Password *</label>
              <input
                type="password"
                value={authForm.password}
                onChange={(e) => setAuthForm(prev => ({ ...prev, password: e.target.value }))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && authForm.email.trim() && authForm.password.trim()) {
                    handleLogin();
                  }
                }}
                placeholder="Your password"
                required
              />
            </div>
            <button 
              className="primary-button"
              onClick={handleLogin} 
              disabled={isLoading || !authForm.email.trim() || !authForm.password.trim()}
            >
              {isLoading ? 'Signing in...' : 'Sign In'}
            </button>
            <p className="auth-switch">
              Don't have an account? <button className="link-button" onClick={() => setCurrentView('signup')}>Sign Up</button>
            </p>
          </div>
        ) : currentView === 'signup' ? (
          <div className="login-section">
            <h2>Create Account</h2>
            <div className="form-group">
              <label>Email Address *</label>
              <input
                type="email"
                value={authForm.email}
                onChange={(e) => setAuthForm(prev => ({ ...prev, email: e.target.value }))}
                placeholder="your.email@institution.edu"
                required
              />
            </div>
            <div className="form-group">
              <label>Password *</label>
              <input
                type="password"
                value={authForm.password}
                onChange={(e) => setAuthForm(prev => ({ ...prev, password: e.target.value }))}
                placeholder="At least 8 characters"
                required
              />
            </div>
            <div className="form-group">
              <label>Confirm Password *</label>
              <input
                type="password"
                value={authForm.confirmPassword}
                onChange={(e) => setAuthForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                placeholder="Confirm your password"
                required
              />
            </div>
            <div className="form-group">
              <label>Full Name *</label>
              <input
                type="text"
                value={authForm.name}
                onChange={(e) => setAuthForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Dr. Jane Smith"
                required
              />
            </div>
            <div className="form-group">
              <label>Institution</label>
              <input
                type="text"
                value={authForm.institution}
                onChange={(e) => setAuthForm(prev => ({ ...prev, institution: e.target.value }))}
                placeholder="State University"
              />
            </div>
            <div className="form-group">
              <label>Department</label>
              <input
                type="text"
                value={authForm.department}
                onChange={(e) => setAuthForm(prev => ({ ...prev, department: e.target.value }))}
                placeholder="Biology"
              />
            </div>
            <button 
              className="primary-button"
              onClick={handleSignup} 
              disabled={isLoading || !authForm.email.trim() || !authForm.password.trim() || !authForm.name.trim()}
            >
              {isLoading ? 'Creating Account...' : 'Create Account'}
            </button>
            <p className="auth-switch">
              Already have an account? <button className="link-button" onClick={() => setCurrentView('login')}>Sign In</button>
            </p>
          </div>
        ) : currentView === 'welcome' ? (
          <div className="welcome-section">
            <h2>Welcome to IACUC Protocol Assistant</h2>
            <div className="welcome-content">
              <p>This system helps you complete IACUC (Institutional Animal Care and Use Committee) protocols with AI assistance.</p>

              <h3>How it works:</h3>
              <p>The system offers two AI-assisted modes for building a protocol:</p>
              <ul>
                <li><strong>Agent Mode:</strong> Chat with an AI agent that drafts and saves your narrative sections as you go.</li>
                <li><strong>Guided Mode:</strong> A step-by-step pass that starts with experimental design and builds through the key AI-assisted sections.</li>
              </ul>
              <p>You can review and edit any answer in <strong>Protocol View</strong>, and view or export the finished protocol in <strong>Summary</strong>.</p>
              <p>
                <strong>AI runs on your own API key:</strong> the AI features use your personal Anthropic API key,
                so usage is billed to your own account. Add your key in <strong>Settings</strong> (a short one-time
                setup) — everything else here works without one.
              </p>

              <h3>⚠️ Important Notice:</h3>
              <div className="warning-box">
                <p><strong>AI results are for guidance only and should not be fully trusted.</strong></p>
                <p>The AI assistant provides suggestions and editorial advice, but you are responsible for:</p>
                <ul>
                  <li>Verifying all information for accuracy</li>
                  <li>Ensuring compliance with institutional policies</li>
                  <li>Reviewing all AI-generated content before submission</li>
                  <li>Following proper IACUC approval processes</li>
                </ul>
                <p>You accept responsibility for sending data to the backend LLM API. More information on the model being used is provided in the landing page.</p>
              </div>
              
              <button 
                className="primary-button"
                onClick={handleWelcomeComplete}
              >
                I Understand - Continue to Dashboard
              </button>
            </div>
          </div>
        ) : currentView === 'landing' ? (
          renderLandingPage()
        ) : currentView === 'settings' ? (
          <SettingsView
            apiBaseUrl={API_BASE_URL}
            getAuthHeaders={getAuthHeaders}
            user={authState.user}
            notice={settingsNotice}
            onBack={() => { setSettingsNotice(null); setCurrentView('landing'); }}
            onApiKeyChanged={(info) => setApiKeyInfo(info)}
          />
        ) : currentView === 'summary' ? (
          <div className="summary-standalone">
            <div className="summary-standalone-header">
              <button className="back-button" onClick={goToLanding}>
                <span className="back-icon">←</span>
                <span className="back-text">Main Menu</span>
              </button>
              <h2 className="summary-standalone-title">{currentProtocol?.title || 'Summary'}</h2>
              {currentProtocol && (
                <button
                  className="secondary-button"
                  onClick={() => selectProtocol(currentProtocol.id)}
                  title="Open this protocol in Protocol View to edit"
                >
                  Protocol View
                </button>
              )}
            </div>
            <SummaryView
              protocolId={currentProtocol?.id || 0}
              protocolTitle={currentProtocol?.title || ''}
              authHeaders={getAuthHeaders()}
            />
          </div>
        ) : currentView === 'dashboard' && authState.user?.is_admin ? (
          <FeedbackDashboard
            apiBaseUrl={API_BASE_URL}
            getAuthHeaders={getAuthHeaders}
            onBack={() => setCurrentView('landing')}
          />
        ) : currentView === 'guided' && currentProtocol ? (
          <GuidedConversation
            protocol={currentProtocol}
            apiBaseUrl={API_BASE_URL}
            getAuthHeaders={getAuthHeaders}
            onComplete={() => {
              setCurrentView('protocol');
              if (currentProtocol) {
                selectProtocol(currentProtocol.id);
              }
            }}
            onSwitchToProtocol={() => {
              // Fetch fresh data from server instead of using stale cached answers
              // This ensures Protocol View shows data saved during Guided Conversation
              if (currentProtocol) {
                selectProtocol(currentProtocol.id);
              }
            }}
            onTitleUpdate={(newTitle) => {
              // Update protocol title in state for immediate UI refresh
              setCurrentProtocol(prev => prev ? { ...prev, title: newTitle } : null);
              // Also refresh protocols list so landing page shows updated title
              loadUserProtocols();
            }}
          />
        ) : currentView === 'agent-chat' && currentProtocol ? (
          <AgentChat
            protocol={currentProtocol}
            apiBaseUrl={API_BASE_URL}
            getAuthHeaders={getAuthHeaders}
            sessionType={agentSessionType}
            onMainMenu={goToLanding}
            onOpenSummary={() => {
              // The shared final-state view; loads fresh data so agent saves are reflected.
              if (currentProtocol) {
                openProtocolSummary(currentProtocol.id);
              }
            }}
            onProtocolChanged={() => {
              // Agent saved an answer (e.g. the title) — refresh the protocols list
              loadUserProtocols();
            }}
          />
        ) : (
          <div className="protocol-workspace">
            <div className={`sidebar ${sidebarPinned ? 'pinned' : 'collapsible'}`}>
              <div className="sidebar-toggle">
                <button 
                  className="pin-toggle-button"
                  onClick={() => setSidebarPinned(!sidebarPinned)}
                  title={sidebarPinned ? 'Auto-collapse sidebar' : 'Pin sidebar open'}
                  aria-label={sidebarPinned ? 'Auto-collapse sidebar' : 'Pin sidebar open'}
                >
                  {sidebarPinned ? '📌' : '📍'}
                </button>
              </div>
              
              <div className="sidebar-header">
                <button className="back-button" onClick={goToLanding}>
                  <span className="back-icon">←</span>
                  <span className="back-text">Back to Protocols</span>
                </button>
                <h3 className="protocol-title">{currentProtocol?.title}</h3>
              </div>
              
              <div className="navigation-section">
                <h4 className="nav-title">Protocol Sections</h4>
                <div className="section-nav">
                  <div 
                    className={`section-item ${currentSection === 1 ? 'active' : ''}`}
                    onClick={() => currentProtocol && loadSection(1, currentProtocol.answers)}
                    title="Section 1: Project"
                  >
                    <span className="section-number">1</span>
                    <span className="section-name">Project</span>
                  </div>
                  <div
                    className={`section-item ${currentSection === 2 ? 'active' : ''}`}
                    onClick={() => currentProtocol && loadSection(2, currentProtocol.answers)}
                    title="Section 2: Personnel"
                  >
                    <span className="section-number">2</span>
                    <span className="section-name">Personnel</span>
                  </div>
                  <div
                    className={`section-item ${currentSection === 3 ? 'active' : ''}`}
                    onClick={() => currentProtocol && loadSection(3, currentProtocol.answers)}
                    title="Section 3: Animals"
                  >
                    <span className="section-number">3</span>
                    <span className="section-name">Animals</span>
                  </div>
                  <div
                    className={`section-item ${currentSection === 4 ? 'active' : ''}`}
                    onClick={() => currentProtocol && loadSection(4, currentProtocol.answers)}
                    title="Section 4: Experimental Design"
                  >
                    <span className="section-number">4</span>
                    <span className="section-name">Experimental Design</span>
                  </div>
                  <div
                    className={`section-item ${currentSection === 5 ? 'active' : ''}`}
                    onClick={() => currentProtocol && loadSection(5, currentProtocol.answers)}
                    title="Section 5: Checklist"
                  >
                    <span className="section-number">5</span>
                    <span className="section-name">Checklist</span>
                  </div>
                  <div
                    className={`section-item ${currentSection === 6 ? 'active' : ''}`}
                    onClick={() => currentProtocol && loadSection(6, currentProtocol.answers)}
                    title="Section 6: Regulatory Requirements"
                  >
                    <span className="section-number">6</span>
                    <span className="section-name">Regulatory Requirements</span>
                  </div>
                  <div
                    className={`section-item ${currentSection === 7 ? 'active' : ''}`}
                    onClick={() => currentProtocol && loadSection(7, currentProtocol.answers)}
                    title="Section 7: Hazards"
                  >
                    <span className="section-number">7</span>
                    <span className="section-name">Hazards</span>
                  </div>
                  
                  <div 
                    className={`section-item ${currentSection === 'summary' ? 'active' : ''}`}
                    onClick={() => loadSection('summary')}
                    title="View complete protocol summary for copy/paste to InfoEd"
                  >
                    <span className="section-number">📋</span>
                    <span className="section-name">Summary</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="main-form">
              {currentProtocol && currentSection === 'summary' ? (
                <SummaryView
                  protocolId={currentProtocol.id}
                  protocolTitle={currentProtocol.title}
                  authHeaders={getAuthHeaders()}
                />
              ) : currentProtocol && currentSection === 2 ? (
                <div className="section-view">
                  <div className="section-header">
                    <h2>Section 2: Personnel</h2>
                  </div>

                  <div className="questions-container">
                    <div className="question-card full-border-test" style={{textAlign: 'center', padding: '3rem 2rem'}}>
                      <div style={{
                        background: 'rgba(253, 208, 35, 0.08)',
                        padding: '2rem',
                        borderRadius: '8px',
                        maxWidth: '600px',
                        margin: '0 auto'
                      }}>
                        <h3 style={{color: '#3E1C84', marginBottom: '1.5rem', fontSize: '1.5rem'}}>
                          ⚠️ Section Not Yet Implemented
                        </h3>
                        <p style={{color: '#50555B', fontSize: '1.1rem', lineHeight: '1.6', marginBottom: '1rem'}}>
                          Personnel information is straightforward data entry that does not benefit from AI assistance.
                        </p>
                        <p style={{color: '#000000', fontSize: '1rem', lineHeight: '1.6'}}>
                          Please enter personnel details directly in <strong>InfoEd</strong> when submitting your final protocol.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : currentProtocol && currentSection === 3 ? (
                <div className="section-view">
                  <div className="section-header">
                    <h2>Section 3: Animals</h2>
                  </div>

                  <div className="questions-container">
                    <div className="question-card full-border-test" style={{textAlign: 'center', padding: '3rem 2rem'}}>
                      <div style={{
                        background: 'rgba(253, 208, 35, 0.08)',
                        padding: '2rem',
                        borderRadius: '8px',
                        maxWidth: '600px',
                        margin: '0 auto'
                      }}>
                        <h3 style={{color: '#3E1C84', marginBottom: '1.5rem', fontSize: '1.5rem'}}>
                          ⚠️ Section Not Yet Implemented
                        </h3>
                        <p style={{color: '#50555B', fontSize: '1.1rem', lineHeight: '1.6', marginBottom: '1rem'}}>
                          Animal information is straightforward data entry that does not benefit from AI assistance.
                        </p>
                        <p style={{color: '#000000', fontSize: '1rem', lineHeight: '1.6'}}>
                          Please ensure animal numbers match with <strong>Question 4.3</strong>.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : currentProtocol && currentSection === 7 ? (
                <div className="section-view">
                  <div className="section-header">
                    <h2>Section 7: Hazards</h2>
                  </div>

                  <div className="questions-container">
                    <div className="question-card full-border-test" style={{textAlign: 'center', padding: '3rem 2rem'}}>
                      <div style={{
                        background: 'rgba(253, 208, 35, 0.08)',
                        padding: '2rem',
                        borderRadius: '8px',
                        maxWidth: '600px',
                        margin: '0 auto'
                      }}>
                        <h3 style={{color: '#3E1C84', marginBottom: '1.5rem', fontSize: '1.5rem'}}>
                          ⚠️ Section Not Yet Implemented
                        </h3>
                        <p style={{color: '#50555B', fontSize: '1.1rem', lineHeight: '1.6', marginBottom: '1rem'}}>
                          Hazards information is straightforward data entry that does not benefit from AI assistance.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : currentProtocol && sectionQuestions.length > 0 ? (
                <div className="section-view">
                  <div className="section-header">
                    <h2>Section {currentSection}: {
                      currentSection === 1 ? 'Project' :
                      currentSection === 2 ? 'Personnel' :
                      currentSection === 3 ? 'Animals' :
                      currentSection === 4 ? 'Experimental Design' :
                      currentSection === 5 ? 'Checklist' :
                      currentSection === 6 ? 'Regulatory Requirements' :
                      currentSection === 7 ? 'Hazards' : 'TBD'
                    }</h2>
                    <p>Complete all questions in this section. Each question can be saved individually.</p>
                  </div>

                  <div className="questions-container">
                    {sectionQuestions.map((question) => {
                      const questionData = sectionAnswers[question.question_number] || {answer: '', followupData: [], acknowledgements: [], cfaJustification: ''};
                      const currentAiSuggestion = aiSuggestions[question.question_number];
                      const isQuestionSaving = isSaving[question.question_number];
                      
                      // Optimized experiment count calculation for Question 4.2
                      const experimentCount = question.question_number === '4.2' && questionData.answer ? 
                        (questionData.answer.match(/EXPERIMENT \d+:/g) || []).length : 0;
                      const experimentLabel = experimentCount > 0 ? 
                        `${experimentCount} Experiment${experimentCount > 1 ? 's' : ''}` : 'Content';
                      
                      return (
                        <React.Fragment key={question.question_number}>
                        <div className={`question-card ${['1.1', '1.2', '1.3', '1.4', '4.1', '4.2', '4.3', '4.4', '4.5', '4.6', '4.7'].includes(question.question_number) ? 'full-border-test' : ''}`}>
                          <div className="question-header">
                            <h3>Question {question.question_number}</h3>
                            <span className="question-type">{question.question_type}</span>
                          </div>
                          
                          <div className="question-content">
                            <h4>{question.question_text}</h4>
                            
                            {question.requirements && (
                              <div className="requirements">
                                <h5>Requirements:</h5>
                                <ReactMarkdown>{question.requirements}</ReactMarkdown>
                              </div>
                            )}
                            
                            {question.example_answers && Array.isArray(question.example_answers) && question.question_number !== '4.4' && (
                              <div className="examples">
                                <div className="examples-header">
                                  <h5>Example Answers</h5>
                                  <button 
                                    type="button"
                                    onClick={() => setShowExamples(prev => ({
                                      ...prev, 
                                      [question.question_number]: !prev[question.question_number]
                                    }))}
                                    className="toggle-examples-btn"
                                  >
                                    {showExamples[question.question_number] ? 'Hide Examples ▲' : 'Show Examples ▼'}
                                  </button>
                                </div>
                                {showExamples[question.question_number] && (
                                  <div className="examples-content">
                                    {question.example_answers.map((example: any, index: number) => (
                                      <div key={index} className="example">
                                        <h6>{example.title}</h6>
                                        <div className="example-content" style={{whiteSpace: 'pre-line'}}>
                                          {example.content}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                          
                          <div className="answer-section">
                            <label>Your Answer:</label>
                            {question.question_number === '4.2' ? (
                              <div className="question-42-interface">
                                {question42State === 'choosing' ? (
                                  // PHASE 1: Mode Selection
                                  <div className="mode-selection-phase">
                                    <h4>Choose Your Approach for Experimental Design</h4>
                                    <p className="phase-description">
                                      Select how you'd like to complete this section. You can always change approaches later.
                                    </p>
                                    <div className="mode-cards">
                                      <div 
                                        className={`mode-card ${question42Mode === 'free_text' ? 'selected' : ''}`}
                                        onClick={() => {
                                          setQuestion42Mode('free_text');
                                          setQuestion42State('building');
                                        }}
                                      >
                                        <div className="mode-icon">📝</div>
                                        <h5>Free Text Mode</h5>
                                        <p>Perfect if you already have an experimental design to paste or prefer writing from scratch.</p>
                                        <ul>
                                          <li>Paste existing content</li>
                                          <li>Write directly in text area</li>
                                          <li>AI helps check completeness</li>
                                        </ul>
                                      </div>
                                      <div 
                                        className={`mode-card ${question42Mode === 'structured' ? 'selected' : ''}`}
                                        onClick={() => {
                                          setQuestion42Mode('structured');
                                          setShowStructuredBuilder(true);
                                          // Ensure structured components are loaded from database
                                          loadStructuredComponents('4.2');
                                        }}
                                      >
                                        <div className="mode-icon">🔨</div>
                                        <h5>Structured Builder</h5>
                                        <p>Step-by-step guidance to build your experimental design from components.</p>
                                        <ul>
                                          <li>Guided component building</li>
                                          <li>AI compiles into paragraphs</li>
                                          <li>Support for multiple experiments</li>
                                        </ul>
                                      </div>
                                    </div>
                                  </div>
                                ) : question42State === 'building' ? (
                                  // PHASE 2: Building Phase
                                  <div className="building-phase">
                                    <div className="phase-header">
                                      <h4>Building Experimental Design - {question42Mode === 'free_text' ? '📝 Free Text Mode' : '🔨 Structured Builder Mode'}</h4>
                                      <button 
                                        className="link-button"
                                        onClick={() => setQuestion42State('choosing')}
                                      >
                                        ← Change Approach
                                      </button>
                                    </div>
                                    
                                    {question42Mode === 'free_text' ? (
                                      <div className="free-text-mode">
                                        <p className="mode-description">
                                          Paste your existing experimental design or write directly. AI will help check completeness and suggest improvements.
                                        </p>
                                        <textarea
                                          value={questionData.answer}
                                          onChange={(e) => updateQuestionAnswer(question.question_number, e.target.value)}
                                          placeholder="Paste your experimental design here or write from scratch. Include hypothesis, timeline, procedures, treatments (dose, route, volume, frequency), and animal numbers..."
                                          rows={12}
                                          className="experimental-design-textarea"
                                        />
                                      </div>
                                    ) : (
                                      <div className="structured-mode">
                                        <p className="mode-description">
                                          Design ONE EXPERIMENT at a time. Fill out the components below, then use AI assistance to compile into professional paragraph form.
                                        </p>
                                        
                                        {/* Current Question 4.2 Content Display */}
                                        {questionData.answer && questionData.answer !== 'test. This didnt fill out.' && (
                                          <div className="saved-experiments-section">
                                            <div className="saved-header">
                                              <h5>📄 Current Question 4.2 Content ({experimentLabel})</h5>
                                              <button 
                                                className="secondary-button small"
                                                onClick={() => setShowExamples(prev => ({...prev, ['4.2-saved']: !prev['4.2-saved']}))}
                                              >
                                                {showExamples['4.2-saved'] ? 'Hide' : 'Show'} Saved Text
                                              </button>
                                            </div>
                                            {showExamples['4.2-saved'] && (
                                              <div className="saved-content">
                                                <p className="saved-text">{questionData.answer}</p>
                                              </div>
                                            )}
                                          </div>
                                        )}
                                        
                                        <div className="structured-components">
                                          <div className="component-section">
                                            <h5>1. Hypothesis</h5>
                                            <textarea
                                              value={structuredComponents[question.question_number]?.hypothesis || ''}
                                              onChange={(e) => updateStructuredComponent(question.question_number, 'hypothesis', e.target.value)}
                                              placeholder="Clearly state your experimental hypothesis..."
                                              rows={2}
                                              className="component-input"
                                            />
                                          </div>
                                          <div className="component-section">
                                            <h5>2. Experimental Groups</h5>
                                            <textarea
                                              value={structuredComponents[question.question_number]?.groups || ''}
                                              onChange={(e) => updateStructuredComponent(question.question_number, 'groups', e.target.value)}
                                              placeholder="List your experimental groups (e.g., Control, Treatment 1, Treatment 2)..."
                                              rows={3}
                                              className="component-input"
                                            />
                                          </div>
                                          <div className="component-section">
                                            <h5>3. Timeline & Procedures</h5>
                                            <textarea
                                              value={structuredComponents[question.question_number]?.timeline || ''}
                                              onChange={(e) => updateStructuredComponent(question.question_number, 'timeline', e.target.value)}
                                              placeholder="Describe the experimental timeline and key procedures (Week 1: baseline, Week 2-4: treatment, etc.)..."
                                              rows={4}
                                              className="component-input"
                                            />
                                          </div>
                                          <div className="component-section">
                                            <h5>4. Treatment Details</h5>
                                            <textarea
                                              value={structuredComponents[question.question_number]?.treatment || ''}
                                              onChange={(e) => updateStructuredComponent(question.question_number, 'treatment', e.target.value)}
                                              placeholder="Specify doses, routes, volumes, frequencies for all treatments..."
                                              rows={3}
                                              className="component-input"
                                            />
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  // PHASE 3: Final Clean View
                                  <div className="final-phase">
                                    <div className="final-header">
                                      <h4>Experimental Design (Final)</h4>
                                      <div className="final-actions">
                                        <button 
                                          className="link-button"
                                          onClick={() => setQuestion42State('building')}
                                        >
                                          ✏️ Edit
                                        </button>
                                        <button 
                                          className="link-button"
                                          onClick={() => {
                                            setQuestion42State('choosing');
                                            setQuestion42Mode('structured');
                                          }}
                                        >
                                          🔨 Edit with Structured Mode
                                        </button>
                                      </div>
                                    </div>
                                    
                                    <div className="final-content">
                                      <textarea
                                        value={questionData.answer}
                                        onChange={(e) => updateQuestionAnswer(question.question_number, e.target.value)}
                                        placeholder="Your experimental design will appear here..."
                                        rows={10}
                                        className="final-textarea"
                                      />
                                    </div>
                                    
                                    <p className="final-description">
                                      This is your finalized experimental design. You can edit directly here, or use the buttons above to return to guided editing modes.
                                    </p>
                                  </div>
                                )}
                              </div>
                            ) : question.question_number === '4.3' ? (
                              <div className="question-43-interface">
                                <div className="animal-numbers-section">
                                  <h5>Animal Numbers</h5>
                                  <div className="number-inputs">
                                    <div className="number-input-group">
                                      <label htmlFor="mice-count">Mice:</label>
                                      <input
                                        type="number"
                                        id="mice-count"
                                        min="0"
                                        value={questionData.followupData[0] || ''}
                                        onChange={(e) => {
                                          const newData = [...questionData.followupData];
                                          newData[0] = e.target.value;
                                          updateQuestionFollowupData(question.question_number, newData);
                                        }}
                                        placeholder="0"
                                        className="number-input"
                                      />
                                    </div>
                                    <div className="number-input-group">
                                      <label htmlFor="rats-count">Rats:</label>
                                      <input
                                        type="number"
                                        id="rats-count"
                                        min="0"
                                        value={questionData.followupData[1] || ''}
                                        onChange={(e) => {
                                          const newData = [...questionData.followupData];
                                          newData[1] = e.target.value;
                                          updateQuestionFollowupData(question.question_number, newData);
                                        }}
                                        placeholder="0"
                                        className="number-input"
                                      />
                                    </div>
                                  </div>
                                  <div className="other-species-section">
                                    <label className="checkbox-label">
                                      <input
                                        type="checkbox"
                                        checked={questionData.followupData[2] === 'Yes'}
                                        onChange={(e) => {
                                          const newData = [...questionData.followupData];
                                          newData[2] = e.target.checked ? 'Yes' : 'No';
                                          updateQuestionFollowupData(question.question_number, newData);
                                        }}
                                      />
                                      Do you have any other Species?
                                    </label>
                                  </div>
                                </div>
                                <div className="justification-section">
                                  <h5>Justification</h5>
                                  <textarea
                                    value={questionData.answer}
                                    onChange={(e) => {
                                      updateQuestionAnswer(question.question_number, e.target.value);
                                    }}
                                    placeholder="Justify or explain how the number of animals requested for this protocol was determined..."
                                    rows={8}
                                    className="justification-textarea"
                                  />
                                </div>
                              </div>
                            ) : question.question_number === '4.4' ? (
                              <div className="question-44-interface">
                                <div className="nonsurgical-questions">
                                  <div className="nonsurgical-question">
                                    <h5>Non-Surgical Procedures</h5>
                                    <p>Will you perform non-surgical procedures in this protocol?</p>
                                    <div className="radio-group">
                                      <label>
                                        <input
                                          type="radio"
                                          name={`nonsurgical-${question.question_number}`}
                                          value="Yes"
                                          checked={questionData.followupData[1] === 'Yes'}
                                          onChange={async (e) => {
                                            const newData = [...questionData.followupData];
                                            newData[1] = e.target.value;
                                            updateQuestionFollowupData(question.question_number, newData);
                                            // Auto-save the selection to database with current data
                                            const currentQuestionData = {
                                              answer: questionData.answer,
                                              followupData: newData
                                            };
                                            await saveQuestionAnswer(question.question_number, currentQuestionData);
                                          }}
                                        />
                                        Yes
                                      </label>
                                      <label>
                                        <input
                                          type="radio"
                                          name={`nonsurgical-${question.question_number}`}
                                          value="No"
                                          checked={questionData.followupData[1] === 'No'}
                                          onChange={async (e) => {
                                            const newData = [...questionData.followupData];
                                            newData[1] = e.target.value;
                                            updateQuestionFollowupData(question.question_number, newData);
                                            // Auto-save the selection to database with current data
                                            const currentQuestionData = {
                                              answer: questionData.answer,
                                              followupData: newData
                                            };
                                            await saveQuestionAnswer(question.question_number, currentQuestionData);
                                          }}
                                        />
                                        No
                                      </label>
                                    </div>
                                  </div>
                                </div>
                                
                                {/* Show non-surgical procedure selector if non-surgical procedures are selected as "Yes" */}
                                {questionData.followupData[1] === 'Yes' && (
                                  <div className="nonsurgical-procedure-selection">
                                    <HierarchicalProcedureSelector
                                      protocolId={currentProtocol!.id}
                                      onSelectionChange={handleProcedureSelectionChange}
                                      onGenerateText={handleGenerateQ44Text}
                                      isLoading={isLoading}
                                    />
                                  </div>
                                )}
                                
                                {/* Show non-surgical description box only when non-surgical procedures are selected as "Yes" */}
                                {questionData.followupData[1] === 'Yes' && (
                                  <div className="main-nonsurgical-description">
                                    <h5>Non-Surgical Procedure Description</h5>
                                    <p>
                                      Use the "Generate Q4.4 Text" button above to auto-populate this section with your selected procedures, 
                                      then edit and add any additional procedures or details specific to your protocol.
                                    </p>
                                    <textarea
                                      value={questionData.answer}
                                      onChange={(e) => {
                                        updateQuestionAnswer(question.question_number, e.target.value);
                                      }}
                                      placeholder="List and describe each non-surgical procedure included in your experimental design..."
                                      rows={8}
                                      className="main-textarea"
                                    />
                                  </div>
                                )}
                                
                                {/* Show anesthetics section only when non-surgical procedures are selected as "Yes" */}
                                {questionData.followupData[1] === 'Yes' && (
                                  <div className="anesthetics-section">
                                    <h5>Anesthetics Required</h5>
                                    <p>Will anesthetics be required for any of the nonsurgical procedures other than genotyping in this protocol?</p>
                                    <div className="radio-group">
                                      <label>
                                        <input
                                          type="radio"
                                          name={`anesthetics-${question.question_number}`}
                                          value="Yes"
                                          checked={questionData.followupData[0] === 'Yes'}
                                          onChange={async (e) => {
                                            const newData = [...questionData.followupData];
                                            newData[0] = e.target.value;
                                            updateQuestionFollowupData(question.question_number, newData);
                                            // Auto-save the selection to database with current data
                                            const currentQuestionData = {
                                              answer: questionData.answer,
                                              followupData: newData
                                            };
                                            await saveQuestionAnswer(question.question_number, currentQuestionData);
                                          }}
                                        />
                                        Yes
                                      </label>
                                      <label>
                                        <input
                                          type="radio"
                                          name={`anesthetics-${question.question_number}`}
                                          value="No"
                                          checked={questionData.followupData[0] === 'No'}
                                          onChange={async (e) => {
                                            const newData = [...questionData.followupData];
                                            newData[0] = e.target.value;
                                            updateQuestionFollowupData(question.question_number, newData);
                                            // Auto-save the selection to database with current data
                                            const currentQuestionData = {
                                              answer: questionData.answer,
                                              followupData: newData
                                            };
                                            await saveQuestionAnswer(question.question_number, currentQuestionData);
                                          }}
                                        />
                                        No
                                      </label>
                                    </div>
                                  </div>
                                )}
                              </div>
                            ) : question.question_number === '4.5' ? (
                              <div className="question-45-interface">
                                <div className="surgery-questions">
                                  <div className="surgery-question">
                                    <h5>Surgical Procedures</h5>
                                    <p>Will you perform surgical procedures in this protocol?</p>
                                    <div className="radio-group">
                                      <label>
                                        <input
                                          type="radio"
                                          name={`surgery-${question.question_number}`}
                                          value="Yes"
                                          checked={questionData.followupData[0] === 'Yes'}
                                          onChange={async (e) => {
                                            const newData = [...questionData.followupData];
                                            newData[0] = e.target.value;
                                            updateQuestionFollowupData(question.question_number, newData);
                                            // Auto-save the selection to database with current data
                                            const currentQuestionData = {
                                              answer: questionData.answer,
                                              followupData: newData
                                            };
                                            await saveQuestionAnswer(question.question_number, currentQuestionData);
                                          }}
                                        />
                                        Yes
                                      </label>
                                      <label>
                                        <input
                                          type="radio"
                                          name={`surgery-${question.question_number}`}
                                          value="No"
                                          checked={questionData.followupData[0] === 'No'}
                                          onChange={async (e) => {
                                            const newData = [...questionData.followupData];
                                            newData[0] = e.target.value;
                                            updateQuestionFollowupData(question.question_number, newData);
                                            // Auto-save the selection to database with current data
                                            const currentQuestionData = {
                                              answer: questionData.answer,
                                              followupData: newData
                                            };
                                            await saveQuestionAnswer(question.question_number, currentQuestionData);
                                          }}
                                        />
                                        No
                                      </label>
                                    </div>
                                  </div>
                                </div>
                                
                                {/* Show surgical procedure selector if surgeries are selected as "Yes" */}
                                {questionData.followupData[0] === 'Yes' && (
                                  <div className="surgical-procedure-selection">
                                    <HierarchicalSurgicalProcedureSelector
                                      protocolId={currentProtocol!.id}
                                      onSelectionChange={handleSurgicalProcedureSelectionChange}
                                      onGenerateText={handleGenerateQ45Text}
                                      isLoading={isLoading}
                                      surgicalDescription={questionData.answer}
                                      onDescriptionChange={(description) => updateQuestionAnswer(question.question_number, description)}
                                      onDescriptionSave={() => saveQuestionAnswer(question.question_number)}
                                      isSaving={isSaving[question.question_number]}
                                    />
                                  </div>
                                )}
                              </div>
                            ) : question.question_number === '4.6' ? (
                              <div className="question-46-interface">
                                <div className="euthanasia-section">
                                  <h5>Euthanasia Methods</h5>
                                  <p className="section-description">
                                    Indicate chosen method(s) of euthanasia and how death will be confirmed (secondary method).
                                    Note: for tissue harvest to confirm death it must be inclusive of vital organs (i.e. heart, lung, or brain).
                                    Methods of euthanasia that are not considered "acceptable" by the latest version of the AVMA Guidelines on Euthanasia require scientific justification.
                                  </p>

                                  {/* Initialize followupData if not exists */}
                                  {(() => {
                                    if (!questionData.followupData || questionData.followupData.length === 0) {
                                      const newData = Array(20).fill(''); // Initialize with empty strings
                                      updateQuestionFollowupData(question.question_number, newData);
                                    }
                                    return null;
                                  })()}

                                  <div className="euthanasia-methods">
                                    {/* CO2 Inhalation */}
                                    <div className="method-section">
                                      <label className="method-checkbox">
                                        <input
                                          type="checkbox"
                                          checked={(questionData.followupData[0] || '') === 'true'}
                                          onChange={(e) => {
                                            const newData = [...(questionData.followupData || [])];
                                            newData[0] = e.target.checked ? 'true' : '';
                                            updateQuestionFollowupData(question.question_number, newData);
                                          }}
                                        />
                                        <span>CO2 Inhalation</span>
                                      </label>

                                      {(questionData.followupData[0] || '') === 'true' && (
                                        <div className="method-details">
                                          <div className="field-group">
                                            <label>Route of Administration:</label>
                                            <select
                                              value={questionData.followupData[1] || ''}
                                              onChange={(e) => {
                                                const newData = [...(questionData.followupData || [])];
                                                newData[1] = e.target.value;
                                                updateQuestionFollowupData(question.question_number, newData);
                                              }}
                                            >
                                              <option value="">Select route...</option>
                                              <option value="Inhaled">Inhaled</option>
                                              <option value="Chamber displacement">Chamber displacement</option>
                                              <option value="Gradual fill chamber">Gradual fill chamber</option>
                                            </select>
                                          </div>
                                          <div className="field-group">
                                            <label>Secondary Method:</label>
                                            <select
                                              value={questionData.followupData[2] || ''}
                                              onChange={(e) => {
                                                const newData = [...(questionData.followupData || [])];
                                                newData[2] = e.target.value;
                                                updateQuestionFollowupData(question.question_number, newData);
                                              }}
                                            >
                                              <option value="">Select secondary method...</option>
                                              <option value="Decapitation">Decapitation</option>
                                              <option value="Cervical Dislocation">Cervical Dislocation</option>
                                              <option value="Tissue Collection">Tissue Collection</option>
                                              <option value="Bilateral thoracotomy">Bilateral thoracotomy</option>
                                            </select>
                                          </div>
                                        </div>
                                      )}
                                    </div>

                                    {/* Anesthetic overdose */}
                                    <div className="method-section">
                                      <label className="method-checkbox">
                                        <input
                                          type="checkbox"
                                          checked={(questionData.followupData[3] || '') === 'true'}
                                          onChange={(e) => {
                                            const newData = [...(questionData.followupData || [])];
                                            newData[3] = e.target.checked ? 'true' : '';
                                            updateQuestionFollowupData(question.question_number, newData);
                                          }}
                                        />
                                        <span>Anesthetic overdose or euthanasia solution</span>
                                      </label>
                                      <p className="method-note">(Note: doses administered must be at least 2-3x the anesthetic dose to be considered an overdose)</p>

                                      {(questionData.followupData[3] || '') === 'true' && (
                                        <div className="method-details">
                                          <div className="field-group">
                                            <label>Drug(s):</label>
                                            <select
                                              value={questionData.followupData[4] || ''}
                                              onChange={(e) => {
                                                const newData = [...(questionData.followupData || [])];
                                                newData[4] = e.target.value;
                                                updateQuestionFollowupData(question.question_number, newData);
                                              }}
                                            >
                                              <option value="">Select drug...</option>
                                              <option value="Pentobarbital">Pentobarbital</option>
                                              <option value="Ketamine/Xylazine">Ketamine/Xylazine</option>
                                              <option value="Euthasol">Euthasol</option>
                                              <option value="Isoflurane overdose">Isoflurane overdose</option>
                                              <option value="Other">Other</option>
                                            </select>
                                            {questionData.followupData[4] === 'Other' && (
                                              <input
                                                type="text"
                                                placeholder="Specify other drug..."
                                                value={questionData.followupData[5] || ''}
                                                onChange={(e) => {
                                                  const newData = [...(questionData.followupData || [])];
                                                  newData[5] = e.target.value;
                                                  updateQuestionFollowupData(question.question_number, newData);
                                                }}
                                              />
                                            )}
                                          </div>
                                          <div className="field-group">
                                            <label>Dose & Route of Administration:</label>
                                            <input
                                              type="text"
                                              placeholder="e.g., 200 mg/kg IP"
                                              value={questionData.followupData[6] || ''}
                                              onChange={(e) => {
                                                const newData = [...(questionData.followupData || [])];
                                                newData[6] = e.target.value;
                                                updateQuestionFollowupData(question.question_number, newData);
                                              }}
                                            />
                                          </div>
                                          <div className="field-group">
                                            <label>Secondary Method:</label>
                                            <select
                                              value={questionData.followupData[7] || ''}
                                              onChange={(e) => {
                                                const newData = [...(questionData.followupData || [])];
                                                newData[7] = e.target.value;
                                                updateQuestionFollowupData(question.question_number, newData);
                                              }}
                                            >
                                              <option value="">Select secondary method...</option>
                                              <option value="Decapitation">Decapitation</option>
                                              <option value="Cervical Dislocation">Cervical Dislocation</option>
                                              <option value="Tissue Collection">Tissue Collection</option>
                                              <option value="Bilateral thoracotomy">Bilateral thoracotomy</option>
                                            </select>
                                          </div>
                                        </div>
                                      )}
                                    </div>

                                    {/* Cervical dislocation with anesthesia */}
                                    <div className="method-section">
                                      <label className="method-checkbox">
                                        <input
                                          type="checkbox"
                                          checked={(questionData.followupData[8] || '') === 'true'}
                                          onChange={(e) => {
                                            const newData = [...(questionData.followupData || [])];
                                            newData[8] = e.target.checked ? 'true' : '';
                                            updateQuestionFollowupData(question.question_number, newData);
                                          }}
                                        />
                                        <span>Cervical dislocation with anesthesia</span>
                                      </label>

                                      {(questionData.followupData[8] || '') === 'true' && (
                                        <div className="method-details">
                                          <div className="field-group">
                                            <label>Drug(s):</label>
                                            <select
                                              value={questionData.followupData[9] || ''}
                                              onChange={(e) => {
                                                const newData = [...(questionData.followupData || [])];
                                                newData[9] = e.target.value;
                                                updateQuestionFollowupData(question.question_number, newData);
                                              }}
                                            >
                                              <option value="">Select drug...</option>
                                              <option value="Isoflurane">Isoflurane</option>
                                              <option value="CO2">CO2</option>
                                              <option value="Ketamine/Xylazine">Ketamine/Xylazine</option>
                                              <option value="Other">Other</option>
                                            </select>
                                            {questionData.followupData[9] === 'Other' && (
                                              <input
                                                type="text"
                                                placeholder="Specify other drug..."
                                                value={questionData.followupData[10] || ''}
                                                onChange={(e) => {
                                                  const newData = [...(questionData.followupData || [])];
                                                  newData[10] = e.target.value;
                                                  updateQuestionFollowupData(question.question_number, newData);
                                                }}
                                              />
                                            )}
                                          </div>
                                          <div className="field-group">
                                            <label>Dose & Route of Administration:</label>
                                            <input
                                              type="text"
                                              placeholder="e.g., 5% inhaled to effect"
                                              value={questionData.followupData[11] || ''}
                                              onChange={(e) => {
                                                const newData = [...(questionData.followupData || [])];
                                                newData[11] = e.target.value;
                                                updateQuestionFollowupData(question.question_number, newData);
                                              }}
                                            />
                                          </div>
                                        </div>
                                      )}
                                    </div>

                                    {/* Cervical dislocation without anesthesia */}
                                    <div className="method-section">
                                      <label className="method-checkbox">
                                        <input
                                          type="checkbox"
                                          checked={(questionData.followupData[12] || '') === 'true'}
                                          onChange={(e) => {
                                            const newData = [...(questionData.followupData || [])];
                                            newData[12] = e.target.checked ? 'true' : '';
                                            updateQuestionFollowupData(question.question_number, newData);
                                          }}
                                        />
                                        <span>Cervical dislocation without anesthesia</span>
                                      </label>

                                      {(questionData.followupData[12] || '') === 'true' && (
                                        <div className="method-details">
                                          <div className="field-group">
                                            <label>Justification:</label>
                                            <textarea
                                              placeholder="Provide scientific justification for this method..."
                                              value={questionData.followupData[13] || ''}
                                              onChange={(e) => {
                                                const newData = [...(questionData.followupData || [])];
                                                newData[13] = e.target.value;
                                                updateQuestionFollowupData(question.question_number, newData);
                                              }}
                                              rows={3}
                                            />
                                          </div>
                                          <div className="field-group">
                                            <label>Who will perform technique and what is their training?</label>
                                            <textarea
                                              placeholder="Describe personnel and their training..."
                                              value={questionData.followupData[14] || ''}
                                              onChange={(e) => {
                                                const newData = [...(questionData.followupData || [])];
                                                newData[14] = e.target.value;
                                                updateQuestionFollowupData(question.question_number, newData);
                                              }}
                                              rows={2}
                                            />
                                          </div>
                                        </div>
                                      )}
                                    </div>

                                    {/* Decapitation with anesthesia or CO2 */}
                                    <div className="method-section">
                                      <label className="method-checkbox">
                                        <input
                                          type="checkbox"
                                          checked={(questionData.followupData[15] || '') === 'true'}
                                          onChange={(e) => {
                                            const newData = [...(questionData.followupData || [])];
                                            newData[15] = e.target.checked ? 'true' : '';
                                            updateQuestionFollowupData(question.question_number, newData);
                                          }}
                                        />
                                        <span>Decapitation with anesthesia or CO2</span>
                                      </label>

                                      {(questionData.followupData[15] || '') === 'true' && (
                                        <div className="method-details">
                                          <div className="field-group">
                                            <label>Drug(s):</label>
                                            <select
                                              value={questionData.followupData[16] || ''}
                                              onChange={(e) => {
                                                const newData = [...(questionData.followupData || [])];
                                                newData[16] = e.target.value;
                                                updateQuestionFollowupData(question.question_number, newData);
                                              }}
                                            >
                                              <option value="">Select drug...</option>
                                              <option value="Isoflurane">Isoflurane</option>
                                              <option value="CO2">CO2</option>
                                              <option value="Ketamine/Xylazine">Ketamine/Xylazine</option>
                                              <option value="Other">Other</option>
                                            </select>
                                            {questionData.followupData[16] === 'Other' && (
                                              <input
                                                type="text"
                                                placeholder="Specify other drug..."
                                                value={questionData.followupData[17] || ''}
                                                onChange={(e) => {
                                                  const newData = [...(questionData.followupData || [])];
                                                  newData[17] = e.target.value;
                                                  updateQuestionFollowupData(question.question_number, newData);
                                                }}
                                              />
                                            )}
                                          </div>
                                          <div className="field-group">
                                            <label>Dose & Route of Administration:</label>
                                            <input
                                              type="text"
                                              placeholder="e.g., 5% inhaled to deep anesthesia"
                                              value={questionData.followupData[18] || ''}
                                              onChange={(e) => {
                                                const newData = [...(questionData.followupData || [])];
                                                newData[18] = e.target.value;
                                                updateQuestionFollowupData(question.question_number, newData);
                                              }}
                                            />
                                          </div>
                                        </div>
                                      )}
                                    </div>

                                    {/* Decapitation without anesthesia */}
                                    <div className="method-section">
                                      <label className="method-checkbox">
                                        <input
                                          type="checkbox"
                                          checked={(questionData.followupData[19] || '') === 'true'}
                                          onChange={(e) => {
                                            const newData = [...(questionData.followupData || [])];
                                            newData[19] = e.target.checked ? 'true' : '';
                                            updateQuestionFollowupData(question.question_number, newData);
                                          }}
                                        />
                                        <span>Decapitation without anesthesia</span>
                                      </label>

                                      {(questionData.followupData[19] || '') === 'true' && (
                                        <div className="method-details">
                                          <div className="field-group">
                                            <label>Justification:</label>
                                            <textarea
                                              placeholder="Provide scientific justification for this method..."
                                              value={questionData.followupData[20] || ''}
                                              onChange={(e) => {
                                                const newData = [...(questionData.followupData || [])];
                                                newData[20] = e.target.value;
                                                updateQuestionFollowupData(question.question_number, newData);
                                              }}
                                              rows={3}
                                            />
                                          </div>
                                          <div className="field-group">
                                            <label>Who will perform technique and what is their training?</label>
                                            <textarea
                                              placeholder="Describe personnel and their training..."
                                              value={questionData.followupData[21] || ''}
                                              onChange={(e) => {
                                                const newData = [...(questionData.followupData || [])];
                                                newData[21] = e.target.value;
                                                updateQuestionFollowupData(question.question_number, newData);
                                              }}
                                              rows={2}
                                            />
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </div>

                                  <div className="section-actions">
                                    <button
                                      onClick={() => saveQuestionAnswer(question.question_number)}
                                      disabled={isSaving[question.question_number]}
                                      className="save-btn"
                                    >
                                      {isSaving[question.question_number] ? 'Saving...' : 'Save Euthanasia Methods'}
                                    </button>
                                  </div>
                                </div>
                              </div>
                            ) : question.question_number === '4.7' ? (
                              <div className="question-47-interface">
                                <div className="controlled-substances-section">
                                  <h5>Controlled Substances</h5>

                                  {/* Initialize followupData if not exists */}
                                  {(() => {
                                    if (!questionData.followupData || questionData.followupData.length === 0) {
                                      const newData = Array(5).fill(''); // Initialize with empty strings
                                      updateQuestionFollowupData(question.question_number, newData);
                                    }
                                    return null;
                                  })()}

                                  <div className="question-group">
                                    <p className="question-text">Are you proposing to use controlled substances for anesthesia, analgesia, or euthanasia (e.g. ketamine, pentobarbital, buprenorphine, Euthasol, etc.) in this protocol?</p>
                                    <div className="radio-group">
                                      <label>
                                        <input
                                          type="radio"
                                          name={`controlled-${question.question_number}`}
                                          value="Yes"
                                          checked={(questionData.followupData[0] || '') === 'Yes'}
                                          onChange={async (e) => {
                                            const newData = [...(questionData.followupData || [])];
                                            newData[0] = e.target.value;
                                            updateQuestionFollowupData(question.question_number, newData);
                                            // Auto-save the selection to database with current data
                                            const currentQuestionData = {
                                              answer: questionData.answer,
                                              followupData: newData
                                            };
                                            await saveQuestionAnswer(question.question_number, currentQuestionData);
                                          }}
                                        />
                                        Yes
                                      </label>
                                      <label>
                                        <input
                                          type="radio"
                                          name={`controlled-${question.question_number}`}
                                          value="No"
                                          checked={(questionData.followupData[0] || '') === 'No'}
                                          onChange={async (e) => {
                                            const newData = [...(questionData.followupData || [])];
                                            newData[0] = e.target.value;
                                            updateQuestionFollowupData(question.question_number, newData);
                                            // Auto-save the selection to database with current data
                                            const currentQuestionData = {
                                              answer: questionData.answer,
                                              followupData: newData
                                            };
                                            await saveQuestionAnswer(question.question_number, currentQuestionData);
                                          }}
                                        />
                                        No
                                      </label>
                                    </div>
                                  </div>

                                  {(questionData.followupData[0] || '') === 'Yes' && (
                                    <div className="followup-section">
                                      <div className="question-group">
                                        <p className="question-text">Do you have a current registration with the Louisiana Board of Pharmacy and the Drug Enforcement Agency?</p>
                                        <div className="radio-group">
                                          <label>
                                            <input
                                              type="radio"
                                              name={`registration-${question.question_number}`}
                                              value="Yes"
                                              checked={(questionData.followupData[1] || '') === 'Yes'}
                                              onChange={async (e) => {
                                                const newData = [...(questionData.followupData || [])];
                                                newData[1] = e.target.value;
                                                updateQuestionFollowupData(question.question_number, newData);
                                                // Auto-save the selection to database with current data
                                                const currentQuestionData = {
                                                  answer: questionData.answer,
                                                  followupData: newData
                                                };
                                                await saveQuestionAnswer(question.question_number, currentQuestionData);
                                              }}
                                            />
                                            Yes
                                          </label>
                                          <label>
                                            <input
                                              type="radio"
                                              name={`registration-${question.question_number}`}
                                              value="No"
                                              checked={(questionData.followupData[1] || '') === 'No'}
                                              onChange={async (e) => {
                                                const newData = [...(questionData.followupData || [])];
                                                newData[1] = e.target.value;
                                                updateQuestionFollowupData(question.question_number, newData);
                                                // Auto-save the selection to database with current data
                                                const currentQuestionData = {
                                                  answer: questionData.answer,
                                                  followupData: newData
                                                };
                                                await saveQuestionAnswer(question.question_number, currentQuestionData);
                                              }}
                                            />
                                            No
                                          </label>
                                        </div>
                                      </div>

                                      <div className="details-section">
                                        <label>Provide details about controlled substances and registration:</label>
                                        <textarea
                                          value={questionData.followupData[2] || ''}
                                          onChange={(e) => {
                                            const newData = [...(questionData.followupData || [])];
                                            newData[2] = e.target.value;
                                            updateQuestionFollowupData(question.question_number, newData);
                                          }}
                                          placeholder="Describe controlled substances to be used, registration details, storage protocols..."
                                          rows={4}
                                        />
                                      </div>
                                    </div>
                                  )}
                                </div>

                                <div className="section-actions">
                                  <button
                                    onClick={() => saveQuestionAnswer(question.question_number)}
                                    disabled={isSaving[question.question_number]}
                                    className="save-btn"
                                  >
                                    {isSaving[question.question_number] ? 'Saving...' : 'Save Controlled Substances'}
                                  </button>
                                </div>
                              </div>
                            ) : question.question_number === '1.5' && currentSection === 1 ? (
                              // Q1.5 - Project Type (Section 1 only)
                              <div className="project-type-section">
                                <div className="project-type-dropdown">
                                  <label htmlFor="project-type-select">Select Project Type:</label>
                                  <select
                                    id="project-type-select"
                                    value={questionData.answer || ''}
                                    onChange={(e) => updateQuestionAnswer(question.question_number, e.target.value)}
                                  >
                                    <option value="">-- Select Type --</option>
                                    <option value="Type A">Type A</option>
                                    <option value="Type B">Type B</option>
                                    <option value="Type C">Type C</option>
                                  </select>
                                </div>

                                {/* Type descriptions */}
                                <div className="project-type-descriptions">
                                  <div className={`type-description ${questionData.answer === 'Type A' ? 'selected' : ''}`}>
                                    <strong>TYPE A</strong> - Pain or distress will not be induced; animals will only be used for injections, collections, or procedures causing nothing more than minor discomfort; or will be humanely euthanized prior to induction of pain or distress.
                                  </div>
                                  <div className={`type-description ${questionData.answer === 'Type B' ? 'selected' : ''}`}>
                                    <strong>TYPE B</strong> - Pain or distress will be relieved by appropriate therapy.
                                  </div>
                                  <div className={`type-description ${questionData.answer === 'Type C' ? 'selected' : ''}`}>
                                    <strong>TYPE C</strong> - Alleviation of pain or distress would interfere with experimental outcomes, i.e. temperature exposures outside the Guide, use of neuromuscular blocking agents, chronic restraint, caloric restriction.
                                  </div>
                                </div>

                                {/* Save button */}
                                <div className="section-actions">
                                  <button
                                    onClick={() => saveQuestionAnswer(question.question_number)}
                                    disabled={isSaving[question.question_number] || !questionData.answer}
                                    className="save-btn"
                                  >
                                    {isSaving[question.question_number] ? 'Saving...' : 'Save'}
                                  </button>
                                </div>
                              </div>
                            ) : (currentSection === 5 || currentSection === 6) ? (
                              // Section 5 & 6 Special Rendering
                              <div className="section-5-6-questions">
                                {/* Display Guidelines (Phase 2 - Q5.4) */}
                                {question.example_answers?.guidelines_display?.show && (
                                  <div className="guidelines-display">
                                    <h4>{question.example_answers.guidelines_display.title}</h4>
                                    <div className="guidelines-content">
                                      <ReactMarkdown>{question.example_answers.guidelines_display.content}</ReactMarkdown>
                                    </div>
                                  </div>
                                )}

                                {/* Blood Volume Calculator (Q5.4 only, when answer is Yes) */}
                                {question.question_number === '5.4' && questionData.answer === 'Yes' && (
                                  <BloodVolumeCalculator />
                                )}

                                {question.question_type === 'yes_no' && question.question_number !== '5.5' && question.question_number !== '5.12' ? (
                                  <>
                                    {/* Yes/No Radio Buttons - AUTO-SAVE */}
                                    <div className="radio-group">
                                      <label>
                                        <input
                                          type="radio"
                                          name={`q-${question.question_number}`}
                                          value="Yes"
                                          checked={questionData.answer === 'Yes'}
                                          onChange={async (e) => {
                                            updateQuestionAnswer(question.question_number, 'Yes');
                                            // HYBRID: Auto-save radio selection
                                            await saveQuestionAnswer(question.question_number, {
                                              answer: 'Yes',
                                              followupData: questionData.followupData
                                            });
                                          }}
                                        />
                                        Yes
                                      </label>
                                      <label>
                                        <input
                                          type="radio"
                                          name={`q-${question.question_number}`}
                                          value="No"
                                          checked={questionData.answer === 'No'}
                                          onChange={async (e) => {
                                            updateQuestionAnswer(question.question_number, 'No');
                                            // HYBRID: Auto-save and clear followup
                                            await saveQuestionAnswer(question.question_number, {
                                              answer: 'No',
                                              followupData: []
                                            });
                                          }}
                                        />
                                        No
                                      </label>
                                    </div>

                                    {/* Conditional Followup Fields - MANUAL SAVE */}
                                    {questionData.answer === 'Yes' && question.example_answers?.followup_config && (
                                      <div className="followup-fields">
                                        {question.example_answers.followup_config.type === 'list' && (
                                          <>
                                            <label>{question.example_answers.followup_config.label}</label>
                                            <textarea
                                              value={questionData.followupData[0] || ''}
                                              onChange={(e) => {
                                                const newData = [e.target.value];
                                                updateQuestionFollowupData(question.question_number, newData);
                                              }}
                                              placeholder={question.example_answers.followup_config.placeholder}
                                              rows={4}
                                            />

                                            {/* Scaffold Button */}
                                            {question.example_answers.scaffold_config && (
                                              <button
                                                onClick={() => generateScaffold(question.question_number)}
                                                disabled={generatingScaffold[question.question_number]}
                                                className="scaffold-btn"
                                              >
                                                {generatingScaffold[question.question_number]
                                                  ? 'Generating...'
                                                  : question.example_answers.scaffold_config.button_text}
                                              </button>
                                            )}

                                            {/* Acknowledgement Checkboxes (Phase 2 - Q5.4, Q5.10) */}
                                            {question.example_answers.acknowledgements && (
                                              <div className="acknowledgements">
                                                {question.example_answers.acknowledgements.map((ack: any) => (
                                                  <label key={ack.id} className="acknowledgement-checkbox">
                                                    <input
                                                      type="checkbox"
                                                      checked={questionData.acknowledgements?.includes(ack.id) || false}
                                                      onChange={(e) => handleAcknowledgementChange(
                                                        question.question_number,
                                                        ack.id,
                                                        e.target.checked,
                                                        ack.mutually_exclusive_with
                                                      )}
                                                    />
                                                    <span className={ack.required ? 'required' : ''}>{ack.text}</span>
                                                  </label>
                                                ))}
                                              </div>
                                            )}

                                            {/* Conditional Followup (Phase 2 - Q5.11 CFA Detection) */}
                                            {question.example_answers.conditional_followup &&
                                             shouldShowConditionalFollowup(
                                               questionData.followupData[0] || '',
                                               question.example_answers.conditional_followup
                                             ) && (
                                              <div className="conditional-followup">
                                                <label className="warning-label">
                                                  {question.example_answers.conditional_followup.label}
                                                </label>
                                                <textarea
                                                  value={questionData.cfaJustification || ''}
                                                  onChange={(e) => updateCfaJustification(question.question_number, e.target.value)}
                                                  placeholder={question.example_answers.conditional_followup.field.placeholder}
                                                  rows={6}
                                                />
                                              </div>
                                            )}

                                            {/* MANUAL SAVE */}
                                            <button
                                              onClick={() => saveQuestionAnswer(question.question_number)}
                                              disabled={isSaving[question.question_number]}
                                              className="save-btn"
                                            >
                                              {isSaving[question.question_number] ? 'Saving...' : 'Save'}
                                            </button>
                                          </>
                                        )}

                                        {question.example_answers.followup_config.type === 'structured_list' && (
                                          <>
                                            {question.example_answers.followup_config.fields.map((field: any, idx: number) => (
                                              <div key={field.name} className="field-group">
                                                <label>{field.label}</label>
                                                {field.type === 'textarea' ? (
                                                  <textarea
                                                    value={questionData.followupData[0]?.[field.name] || ''}
                                                    onChange={(e) => {
                                                      const newData = [...(questionData.followupData || [{}])];
                                                      if (!newData[0]) newData[0] = {};
                                                      newData[0] = { ...newData[0], [field.name]: e.target.value };
                                                      updateQuestionFollowupData(question.question_number, newData);
                                                    }}
                                                    placeholder={field.placeholder}
                                                    rows={3}
                                                  />
                                                ) : (
                                                  <input
                                                    type="text"
                                                    value={questionData.followupData[0]?.[field.name] || ''}
                                                    onChange={(e) => {
                                                      const newData = [...(questionData.followupData || [{}])];
                                                      if (!newData[0]) newData[0] = {};
                                                      newData[0] = { ...newData[0], [field.name]: e.target.value };
                                                      updateQuestionFollowupData(question.question_number, newData);
                                                    }}
                                                    placeholder={field.placeholder}
                                                  />
                                                )}
                                              </div>
                                            ))}

                                            {/* Scaffold Button */}
                                            {question.example_answers.scaffold_config && (
                                              <button
                                                onClick={() => generateScaffold(question.question_number)}
                                                disabled={generatingScaffold[question.question_number]}
                                                className="scaffold-btn"
                                              >
                                                {generatingScaffold[question.question_number]
                                                  ? 'Generating...'
                                                  : question.example_answers.scaffold_config.button_text}
                                              </button>
                                            )}

                                            {/* Acknowledgement Checkboxes (Phase 2 - Q5.4, Q5.10) */}
                                            {question.example_answers.acknowledgements && (
                                              <div className="acknowledgements">
                                                {question.example_answers.acknowledgements.map((ack: any) => (
                                                  <label key={ack.id} className="acknowledgement-checkbox">
                                                    <input
                                                      type="checkbox"
                                                      checked={questionData.acknowledgements?.includes(ack.id) || false}
                                                      onChange={(e) => handleAcknowledgementChange(
                                                        question.question_number,
                                                        ack.id,
                                                        e.target.checked,
                                                        ack.mutually_exclusive_with
                                                      )}
                                                    />
                                                    <span className={ack.required ? 'required' : ''}>{ack.text}</span>
                                                  </label>
                                                ))}
                                              </div>
                                            )}

                                            {/* Conditional Followup (Phase 2 - Q5.11 CFA Detection) */}
                                            {question.example_answers.conditional_followup &&
                                             shouldShowConditionalFollowup(
                                               questionData.followupData[0] || '',
                                               question.example_answers.conditional_followup
                                             ) && (
                                              <div className="conditional-followup">
                                                <label className="warning-label">
                                                  {question.example_answers.conditional_followup.label}
                                                </label>
                                                <textarea
                                                  value={questionData.cfaJustification || ''}
                                                  onChange={(e) => updateCfaJustification(question.question_number, e.target.value)}
                                                  placeholder={question.example_answers.conditional_followup.field.placeholder}
                                                  rows={6}
                                                />
                                              </div>
                                            )}

                                            {/* MANUAL SAVE */}
                                            <button
                                              onClick={() => saveQuestionAnswer(question.question_number)}
                                              disabled={isSaving[question.question_number]}
                                              className="save-btn"
                                            >
                                              {isSaving[question.question_number] ? 'Saving...' : 'Save'}
                                            </button>
                                          </>
                                        )}
                                      </div>
                                    )}
                                  </>
                                ) : question.question_number === '5.5' ? (
                                  // SPECIAL: Q5.5 Compound Administration - Chained conditionals with repeatable compound list
                                  <>
                                    {/* Yes/No Radio Buttons - AUTO-SAVE */}
                                    <div className="radio-group">
                                      <label>
                                        <input
                                          type="radio"
                                          name={`q-${question.question_number}`}
                                          value="Yes"
                                          checked={questionData.answer === 'Yes'}
                                          onChange={async (e) => {
                                            updateQuestionAnswer(question.question_number, 'Yes');
                                            // HYBRID: Auto-save radio selection
                                            await saveQuestionAnswer(question.question_number, {
                                              answer: 'Yes',
                                              followupData: questionData.followupData
                                            });
                                          }}
                                        />
                                        Yes
                                      </label>
                                      <label>
                                        <input
                                          type="radio"
                                          name={`q-${question.question_number}`}
                                          value="No"
                                          checked={questionData.answer === 'No'}
                                          onChange={async (e) => {
                                            updateQuestionAnswer(question.question_number, 'No');
                                            // HYBRID: Auto-save radio selection
                                            await saveQuestionAnswer(question.question_number, {
                                              answer: 'No',
                                              followupData: []
                                            });
                                          }}
                                        />
                                        No
                                      </label>
                                    </div>

                                    {/* Followup section (only when answer is Yes) */}
                                    {questionData.answer === 'Yes' && (
                                      <div className="compound-administration">
                                        {/* Display OLAW guidelines */}
                                        {question.example_answers.guidelines_display?.show && (
                                          <div className="guidelines-display">
                                            <h5>OLAW Requirements</h5>
                                            <ReactMarkdown>{question.example_answers.guidelines_display.content}</ReactMarkdown>
                                          </div>
                                        )}

                                        {/* Chained conditional levels */}
                                        {question.example_answers.followup_config?.levels && (
                                      <div className="chained-conditionals">
                                        {question.example_answers.followup_config.levels.map((level: any, levelIndex: number) => {
                                          // Show level if it's the first one OR if previous level was "Yes"
                                          const previousLevel = levelIndex > 0 ? question.example_answers.followup_config.levels[levelIndex - 1] : null;
                                          const previousValue = previousLevel ? questionData.followupData[0]?.[previousLevel.field_name] : null;
                                          const shouldShow = levelIndex === 0 || previousValue === 'Yes';

                                          if (!shouldShow) return null;

                                          return (
                                            <div key={level.field_name} className="conditional-level">
                                              <label className="conditional-question">{level.question}</label>
                                              <div className="yes-no-inline">
                                                <label className="radio-option">
                                                  <input
                                                    type="radio"
                                                    name={`${question.question_number}-${level.field_name}`}
                                                    value="Yes"
                                                    checked={questionData.followupData[0]?.[level.field_name] === 'Yes'}
                                                    onChange={async (e) => {
                                                      const newData = [...questionData.followupData];
                                                      if (!newData[0]) newData[0] = { compounds: [] };
                                                      newData[0] = { ...newData[0], [level.field_name]: 'Yes' };
                                                      updateQuestionFollowupData(question.question_number, newData);
                                                      // AUTO-SAVE: Save immediately on radio change
                                                      await saveQuestionAnswer(question.question_number, {
                                                        answer: questionData.answer,
                                                        followupData: newData
                                                      });
                                                    }}
                                                  />
                                                  Yes
                                                </label>
                                                <label className="radio-option">
                                                  <input
                                                    type="radio"
                                                    name={`${question.question_number}-${level.field_name}`}
                                                    value="No"
                                                    checked={questionData.followupData[0]?.[level.field_name] === 'No'}
                                                    onChange={async (e) => {
                                                      const newData = [...questionData.followupData];
                                                      if (!newData[0]) newData[0] = { compounds: [] };
                                                      newData[0] = { ...newData[0], [level.field_name]: 'No' };
                                                      updateQuestionFollowupData(question.question_number, newData);
                                                      // AUTO-SAVE: Save immediately on radio change
                                                      await saveQuestionAnswer(question.question_number, {
                                                        answer: questionData.answer,
                                                        followupData: newData
                                                      });
                                                    }}
                                                  />
                                                  No
                                                </label>
                                              </div>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    )}

                                    {/* Compound list (only shown if dilution_only === "No") */}
                                    {questionData.followupData[0]?.dilution_only === 'No' && question.example_answers.followup_config?.compound_list && (
                                      <div className="compound-list-section">
                                        <h5>Non-Pharmaceutical Grade Compounds (other than dilution)</h5>
                                        <p className="help-text">
                                          List all non-pharmaceutical grade compounds and provide justification for each.
                                          Use the "Scan Protocol" button to automatically detect compounds mentioned elsewhere.
                                        </p>

                                        {/* AI Compound Scanning Button */}
                                        {question.example_answers.scaffold_config && (
                                          <div className="ai-scan-section">
                                            <button
                                              onClick={() => generateScaffold(question.question_number)}
                                              disabled={generatingScaffold[question.question_number]}
                                              className="scaffold-btn compound-scan"
                                              type="button"
                                            >
                                              {generatingScaffold[question.question_number]
                                                ? 'Scanning Protocol...'
                                                : question.example_answers.scaffold_config.button_text}
                                            </button>
                                            {question.example_answers.scaffold_config.help_text && (
                                              <p className="scaffold-help">{question.example_answers.scaffold_config.help_text}</p>
                                            )}
                                          </div>
                                        )}

                                        {/* Repeatable compound list */}
                                        <div className="repeatable-compound-list">
                                          {(questionData.followupData[0]?.compounds || []).map((compound: any, compoundIndex: number) => (
                                            <div key={compoundIndex} className="compound-entry">
                                              <div className="entry-header">
                                                <h6>Compound {compoundIndex + 1}</h6>
                                                <button
                                                  type="button"
                                                  onClick={() => {
                                                    const newData = [...questionData.followupData];
                                                    const compounds = (newData[0]?.compounds || []).filter((_: any, i: number) => i !== compoundIndex);
                                                    newData[0] = { ...newData[0], compounds };
                                                    updateQuestionFollowupData(question.question_number, newData);
                                                  }}
                                                  className="remove-button"
                                                >
                                                  Remove
                                                </button>
                                              </div>
                                              <div className="entry-fields">
                                                {question.example_answers.followup_config.compound_list.fields.map((field: any) => (
                                                  <div key={field.name} className="field-group">
                                                    <label>{field.label}</label>
                                                    {field.type === 'textarea' ? (
                                                      <textarea
                                                        value={compound[field.name] || ''}
                                                        onChange={(e) => {
                                                          const newData = [...questionData.followupData];
                                                          const compounds = [...(newData[0]?.compounds || [])];
                                                          compounds[compoundIndex] = {
                                                            ...compounds[compoundIndex],
                                                            [field.name]: e.target.value
                                                          };
                                                          newData[0] = { ...newData[0], compounds };
                                                          updateQuestionFollowupData(question.question_number, newData);
                                                        }}
                                                        placeholder={field.placeholder}
                                                        rows={field.rows || 3}
                                                      />
                                                    ) : (
                                                      <input
                                                        type="text"
                                                        value={compound[field.name] || ''}
                                                        onChange={(e) => {
                                                          const newData = [...questionData.followupData];
                                                          const compounds = [...(newData[0]?.compounds || [])];
                                                          compounds[compoundIndex] = {
                                                            ...compounds[compoundIndex],
                                                            [field.name]: e.target.value
                                                          };
                                                          newData[0] = { ...newData[0], compounds };
                                                          updateQuestionFollowupData(question.question_number, newData);
                                                        }}
                                                        placeholder={field.placeholder}
                                                      />
                                                    )}
                                                  </div>
                                                ))}
                                              </div>
                                            </div>
                                          ))}

                                          {/* Add New Compound button */}
                                          <button
                                            type="button"
                                            onClick={() => {
                                              const newData = [...questionData.followupData];
                                              if (!newData[0]) newData[0] = { compounds: [] };
                                              if (!newData[0].compounds) newData[0].compounds = [];
                                              const newCompound: any = {};
                                              question.example_answers.followup_config.compound_list.fields.forEach((field: any) => {
                                                newCompound[field.name] = '';
                                              });
                                              newData[0].compounds.push(newCompound);
                                              updateQuestionFollowupData(question.question_number, newData);
                                            }}
                                            className="add-button"
                                          >
                                            {question.example_answers.followup_config.compound_list.add_button_text || 'Add New Compound'}
                                          </button>
                                        </div>

                                        {/* MANUAL SAVE for compound list */}
                                        <button
                                          onClick={() => saveQuestionAnswer(question.question_number)}
                                          disabled={isSaving[question.question_number]}
                                          className="save-btn"
                                        >
                                          {isSaving[question.question_number] ? 'Saving...' : 'Save Compound List'}
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                )}
                                  </>
                                ) : question.question_number === '5.8' ? (
                                  // SPECIAL: 5.8 Adverse Effects (Most Important) - TWO-BUTTON WORKFLOW
                                  <div className="adverse-effects-section">
                                    <textarea
                                      value={questionData.answer}
                                      onChange={(e) => updateQuestionAnswer(question.question_number, e.target.value)}
                                      placeholder="Describe potential adverse effects, monitoring protocols, intervention criteria, and humane endpoints..."
                                      rows={12}
                                      className="large-textarea"
                                    />

                                    {/* AI Scaffold Generation */}
                                    <div className="ai-workflow-buttons">
                                      {/* Generate Scaffold button - always available */}
                                      <div className="scaffold-section">
                                        <button
                                          onClick={() => generateScaffold(question.question_number)}
                                          disabled={generatingScaffold[question.question_number]}
                                          className="scaffold-btn primary"
                                        >
                                          {generatingScaffold[question.question_number]
                                            ? 'Generating adverse effects list...'
                                            : 'Generate potential adverse effects list'}
                                        </button>
                                        <p className="scaffold-help">
                                          AI analyzes your procedures (Q4.2, Q4.4, Q4.5) to help with your monitoring plan:<br/>
                                          • <strong>Empty textarea</strong>: Generates a thoughtful starting point<br/>
                                          • <strong>Your notes/requests</strong>: Expands them into professional IACUC text<br/>
                                          • <strong>Draft text</strong>: Reviews and provides additional suggestions<br/>
                                          <br/>
                                          You can edit the result and click again for review.
                                        </p>
                                      </div>
                                    </div>

                                    {/* MANUAL SAVE */}
                                    <button
                                      onClick={() => saveQuestionAnswer(question.question_number)}
                                      disabled={isSaving[question.question_number]}
                                      className="save-btn"
                                    >
                                      {isSaving[question.question_number] ? 'Saving...' : 'Save Answer'}
                                    </button>
                                  </div>
                                ) : question.question_number === '6.1' ? (
                                  // SPECIAL: 6.1 Non-Duplication with Database Searches
                                  <div className="non-duplication-section">
                                    {/* Narrative text */}
                                    <div className="field-group">
                                      <label>Narrative Statement</label>
                                      <textarea
                                        value={questionData.answer}
                                        onChange={(e) => updateQuestionAnswer(question.question_number, e.target.value)}
                                        placeholder="Provide statement that experiments are not unnecessarily duplicative..."
                                        rows={6}
                                      />

                                      {/* Scaffold Button */}
                                      {question.example_answers?.scaffold_config && (
                                        <button
                                          onClick={() => generateScaffold(question.question_number)}
                                          disabled={generatingScaffold[question.question_number]}
                                          className="scaffold-btn"
                                        >
                                          {generatingScaffold[question.question_number]
                                            ? 'Generating...'
                                            : 'Generate Non-Duplication Statement'}
                                        </button>
                                      )}
                                    </div>

                                    {/* Database Search Yes/No - AUTO-SAVE */}
                                    <div className="field-group">
                                      <label>Did you use an electronic database search?</label>
                                      <div className="radio-group">
                                        <label>
                                          <input
                                            type="radio"
                                            name="db-search-6.1"
                                            value="Yes"
                                            checked={questionData.followupData[0] === 'Yes'}
                                            onChange={async (e) => {
                                              const newData = ['Yes'];
                                              updateQuestionFollowupData(question.question_number, newData);
                                              // Auto-save radio
                                              await saveQuestionAnswer(question.question_number, {
                                                answer: questionData.answer,
                                                followupData: newData
                                              });
                                            }}
                                          />
                                          Yes
                                        </label>
                                        <label>
                                          <input
                                            type="radio"
                                            name="db-search-6.1"
                                            value="No"
                                            checked={questionData.followupData[0] === 'No'}
                                            onChange={async (e) => {
                                              const newData = ['No'];
                                              updateQuestionFollowupData(question.question_number, newData);
                                              // Auto-save radio
                                              await saveQuestionAnswer(question.question_number, {
                                                answer: questionData.answer,
                                                followupData: newData
                                              });
                                            }}
                                          />
                                          No
                                        </label>
                                      </div>
                                    </div>

                                    {/* PLACEHOLDER: Repeatable database search entries */}
                                    {questionData.followupData[0] === 'Yes' && (
                                      <div className="database-searches placeholder">
                                        <p className="info-message">
                                          📋 Database search entry management will be added in Phase 2.
                                          For now, please include search details in your narrative statement above.
                                        </p>
                                      </div>
                                    )}

                                    {/* MANUAL SAVE */}
                                    <button
                                      onClick={() => saveQuestionAnswer(question.question_number)}
                                      disabled={isSaving[question.question_number]}
                                      className="save-btn"
                                    >
                                      {isSaving[question.question_number] ? 'Saving...' : 'Save'}
                                    </button>
                                  </div>
                                ) : question.question_number === '5.12' ? (
                                  // Q5.12 Exotic Species - Display-only (no user input needed, see Requirements above)
                                  <div style={{
                                    padding: '0.75rem',
                                    backgroundColor: '#f5f5f5',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px',
                                    color: '#666',
                                    fontStyle: 'italic'
                                  }}>
                                    No response needed. See Requirements above.
                                  </div>
                                ) : question.question_number === '6.2' ? (
                                  // Q6.2 - Alternatives to Procedures (conditional on Q1.5)
                                  (() => {
                                    // Get Q1.5 answer for conditional rendering
                                    const q15Answer = currentProtocol?.answers?.find(a => a.question_number === '1.5')?.answer || '';
                                    const showFullInterface = q15Answer === 'Type B' || q15Answer === 'Type C';
                                    const isTypeA = q15Answer === 'Type A';

                                    return (
                                      <div className="alternatives-section">
                                        {!q15Answer && (
                                          <div className="alternatives-not-required">
                                            Please complete Q1.5 (Project Type) first to determine if alternatives documentation is required.
                                          </div>
                                        )}

                                        {isTypeA && (
                                          <div className="alternatives-not-required">
                                            <strong>Not Required:</strong> Alternatives documentation is not required for Type A projects
                                            (procedures causing nothing more than minor discomfort). This question applies to Type B and Type C projects only.
                                          </div>
                                        )}

                                        {showFullInterface && (
                                          <>
                                            {/* Instructions */}
                                            <div className="alternatives-instructions">
                                              <p>
                                                <strong>Federal regulations mandate</strong> that you provide a written narrative statement
                                                indicating that you have searched for and considered alternatives for procedures causing
                                                more than momentary pain and distress.
                                              </p>
                                              <p>In your narrative statement address the following:</p>
                                              <ul>
                                                <li>Indicate that you have searched for and considered alternatives.</li>
                                                <li>What are possible alternatives?</li>
                                                <li>Why the alternatives are not appropriate.</li>
                                              </ul>
                                            </div>

                                            {/* Narrative textarea */}
                                            <div className="alternatives-narrative">
                                              <label htmlFor="alternatives-narrative-text">
                                                <strong>Alternative Narrative:</strong>
                                              </label>
                                              <textarea
                                                id="alternatives-narrative-text"
                                                value={questionData.answer}
                                                onChange={(e) => updateQuestionAnswer(question.question_number, e.target.value)}
                                                placeholder="Describe the alternatives you have considered and why they are not appropriate for this protocol..."
                                                rows={8}
                                              />
                                            </div>

                                            {/* Database search radio */}
                                            <div className="alternatives-database-search">
                                              <h5>Is your source from electronic databases?</h5>
                                              <div className="radio-group">
                                                <label>
                                                  <input
                                                    type="radio"
                                                    name={`db-search-${question.question_number}`}
                                                    value="Yes"
                                                    checked={questionData.followupData?.[0] === 'Yes'}
                                                    onChange={async (e) => {
                                                      updateQuestionFollowupData(question.question_number, ['Yes']);
                                                      // Auto-save radio selection (Section 5/6 pattern)
                                                      await saveQuestionAnswer(question.question_number, {
                                                        answer: questionData.answer,
                                                        followupData: ['Yes']
                                                      });
                                                    }}
                                                  />
                                                  Yes
                                                </label>
                                                <label>
                                                  <input
                                                    type="radio"
                                                    name={`db-search-${question.question_number}`}
                                                    value="No"
                                                    checked={questionData.followupData?.[0] === 'No'}
                                                    onChange={async (e) => {
                                                      updateQuestionFollowupData(question.question_number, ['No']);
                                                      // Auto-save radio selection (Section 5/6 pattern)
                                                      await saveQuestionAnswer(question.question_number, {
                                                        answer: questionData.answer,
                                                        followupData: ['No']
                                                      });
                                                    }}
                                                  />
                                                  No
                                                </label>
                                              </div>

                                              {/* Show Phase 2 note if Yes selected */}
                                              {questionData.followupData?.[0] === 'Yes' && (
                                                <div className="phase-2-note">
                                                  <em>Database search entry management will be added in Phase 2.</em>
                                                </div>
                                              )}
                                            </div>

                                            {/* Manual save button for narrative */}
                                            <div className="section-actions">
                                              <button
                                                onClick={() => saveQuestionAnswer(question.question_number)}
                                                disabled={isSaving[question.question_number]}
                                                className="save-btn"
                                              >
                                                {isSaving[question.question_number] ? 'Saving...' : 'Save'}
                                              </button>
                                            </div>
                                          </>
                                        )}
                                      </div>
                                    );
                                  })()
                                ) : (
                                  // Default rendering for other Section 5/6 questions
                                  <textarea
                                    value={questionData.answer}
                                    onChange={(e) => updateQuestionAnswer(question.question_number, e.target.value)}
                                    rows={8}
                                  />
                                )}
                              </div>
                            ) : question.question_type === 'yes_no' ? (
                              <div className="radio-group">
                                <label>
                                  <input
                                    type="radio"
                                    name={`question-${question.question_number}`}
                                    value="Yes"
                                    checked={questionData.answer === 'Yes'}
                                    onChange={(e) => updateQuestionAnswer(question.question_number, e.target.value)}
                                  />
                                  Yes
                                </label>
                                <label>
                                  <input
                                    type="radio"
                                    name={`question-${question.question_number}`}
                                    value="No"
                                    checked={questionData.answer === 'No'}
                                    onChange={(e) => updateQuestionAnswer(question.question_number, e.target.value)}
                                  />
                                  No
                                </label>
                              </div>
                            ) : (
                              <textarea
                                value={questionData.answer}
                                onChange={(e) => {
                                  // Update UI immediately for responsiveness
                                  updateQuestionAnswer(question.question_number, e.target.value);
                                }}
                                placeholder="Enter your answer here..."
                                rows={question.question_type === 'complex' ? 8 : 3}
                              />
                            )}
                            
                            {/* Conditional follow-up fields for yes/no questions (exclude Section 5/6 which have their own rendering) */}
                            {question.question_type === 'yes_no' &&
                             question.example_answers?.followup_config &&
                             questionData.answer === question.example_answers.followup_config.show_on &&
                             currentSection !== 5 && currentSection !== 6 && (
                              <div className="followup-section">
                                <h5>{question.example_answers.followup_config.label}</h5>
                                
                                {question.example_answers.followup_config.type === 'list' ? (
                                  <div className="followup-list">
                                    {questionData.followupData.map((value: string, index: number) => (
                                      <div key={index} className="followup-item">
                                        <input
                                          type="text"
                                          value={value}
                                          onChange={(e) => {
                                            const newData = [...questionData.followupData];
                                            newData[index] = e.target.value;
                                            updateQuestionFollowupData(question.question_number, newData);
                                          }}
                                          placeholder={question.example_answers.followup_config.placeholder}
                                        />
                                        {questionData.followupData.length > 1 && (
                                          <button
                                            type="button"
                                            onClick={() => {
                                              const newData = questionData.followupData.filter((_: any, i: number) => i !== index);
                                              updateQuestionFollowupData(question.question_number, newData);
                                            }}
                                            className="remove-button"
                                          >
                                            Remove
                                          </button>
                                        )}
                                      </div>
                                    ))}
                                    {questionData.followupData.length < (question.example_answers.followup_config.max_fields || 10) && (
                                      <button
                                        type="button"
                                        onClick={() => updateQuestionFollowupData(question.question_number, [...questionData.followupData, ''])}
                                        className="add-button"
                                      >
                                        Add Another
                                      </button>
                                    )}
                                  </div>
                                ) : question.example_answers.followup_config.type === 'structured_list' ? (
                                  <div className="followup-structured-list">
                                    {questionData.followupData.map((entry: any, entryIndex: number) => (
                                      <div key={entryIndex} className="followup-entry">
                                        <div className="entry-header">
                                          <h6>Entry {entryIndex + 1}</h6>
                                          {questionData.followupData.length > 1 && (
                                            <button
                                              type="button"
                                              onClick={() => {
                                                const newData = questionData.followupData.filter((_: any, i: number) => i !== entryIndex);
                                                updateQuestionFollowupData(question.question_number, newData);
                                              }}
                                              className="remove-button"
                                            >
                                              Remove
                                            </button>
                                          )}
                                        </div>
                                        <div className="entry-fields">
                                          {question.example_answers.followup_config.fields.map((field: any) => {
                                            // Check if field should be shown (conditional rendering for nested fields)
                                            if (field.show_when) {
                                              const conditionField = field.show_when.field;
                                              const conditionValue = field.show_when.value;
                                              const actualValue = entry[conditionField];
                                              if (actualValue !== conditionValue) {
                                                return null; // Don't show this field
                                              }
                                            }

                                            return (
                                              <div key={field.name} className="field-group">
                                                <label>{field.label}</label>
                                                {field.type === 'yes_no_inline' ? (
                                                  <div className="yes-no-inline">
                                                    <label className="radio-option">
                                                      <input
                                                        type="radio"
                                                        name={`${question.question_number}-${entryIndex}-${field.name}`}
                                                        value="Yes"
                                                        checked={entry[field.name] === 'Yes'}
                                                        onChange={(e) => {
                                                          const newData = [...questionData.followupData];
                                                          newData[entryIndex] = {
                                                            ...newData[entryIndex],
                                                            [field.name]: e.target.value
                                                          };
                                                          updateQuestionFollowupData(question.question_number, newData);
                                                        }}
                                                      />
                                                      Yes
                                                    </label>
                                                    <label className="radio-option">
                                                      <input
                                                        type="radio"
                                                        name={`${question.question_number}-${entryIndex}-${field.name}`}
                                                        value="No"
                                                        checked={entry[field.name] === 'No'}
                                                        onChange={(e) => {
                                                          const newData = [...questionData.followupData];
                                                          newData[entryIndex] = {
                                                            ...newData[entryIndex],
                                                            [field.name]: e.target.value
                                                          };
                                                          updateQuestionFollowupData(question.question_number, newData);
                                                        }}
                                                      />
                                                      No
                                                    </label>
                                                  </div>
                                                ) : field.type === 'textarea' ? (
                                                  <textarea
                                                    value={entry[field.name] || ''}
                                                    onChange={(e) => {
                                                      const newData = [...questionData.followupData];
                                                      newData[entryIndex] = {
                                                        ...newData[entryIndex],
                                                        [field.name]: e.target.value
                                                      };
                                                      updateQuestionFollowupData(question.question_number, newData);
                                                    }}
                                                    placeholder={field.placeholder}
                                                  />
                                                ) : (
                                                  <input
                                                    type="text"
                                                    value={entry[field.name] || ''}
                                                    onChange={(e) => {
                                                      const newData = [...questionData.followupData];
                                                      newData[entryIndex] = {
                                                        ...newData[entryIndex],
                                                        [field.name]: e.target.value
                                                      };
                                                      updateQuestionFollowupData(question.question_number, newData);
                                                    }}
                                                    placeholder={field.placeholder}
                                                  />
                                                )}
                                              </div>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    ))}
                                    {questionData.followupData.length < (question.example_answers.followup_config.max_entries || 5) && (
                                      <button
                                        type="button"
                                        onClick={() => {
                                          const newEntry: any = {};
                                          question.example_answers.followup_config.fields.forEach((field: any) => {
                                            newEntry[field.name] = '';
                                          });
                                          updateQuestionFollowupData(question.question_number, [...questionData.followupData, newEntry]);
                                        }}
                                        className="add-button"
                                      >
                                        Add Another Entry
                                      </button>
                                    )}
                                  </div>
                                ) : null}
                              </div>
                            )}
                            
                            {/* Action buttons - exclude questions that handle their own buttons */}
                            {!['1.5', '4.5', '4.6', '4.7', '5.3', '5.4', '5.5', '5.8', '5.12', '6.1', '6.2'].includes(question.question_number) && (
                              <div className="action-buttons">
                                {question.question_type === 'complex' && (
                                  <button
                                    onClick={() => getQuestionAiAssistance(question.question_number)}
                                    disabled={isQuestionSaving || (question.question_number === '4.2' && question42Mode === 'structured' ? false : !questionData.answer.trim())}
                                    className="accent-button"
                                  >
                                    {isQuestionSaving ? (
                                      <span className="ai-loading-message">
                                        <span className="spinner dark"></span>
                                        AI is reviewing... (15-30 seconds)
                                      </span>
                                    ) : (
                                      question.question_number === '4.2' && question42Mode === 'free_text'
                                        ? 'AI Review of Full Experimental Design'
                                        : 'Get AI Assistance'
                                    )}
                                  </button>
                                )}
                                
                                {/* Save Answer Button for all questions except 4.5 */}
                                <button 
                                  onClick={() => saveQuestionAnswer(question.question_number)} 
                                  disabled={isSaving[question.question_number] || !questionData.answer?.trim()}
                                  className="primary-button"
                                >
                                  {isSaving[question.question_number] ? '💾 Saving...' : 'Save Answer'}
                                </button>
                              </div>
                            )}

                            {/* Mode 2: AI Review Feedback (editable) */}
                            {showReviewFeedback[question.question_number] && aiReviewFeedback[question.question_number] && (
                              <div className="ai-review-feedback-container">
                                <div className="ai-review-feedback-header">
                                  <span>AI Review Feedback (Editable)</span>
                                  <button
                                    className="close-feedback-btn"
                                    onClick={() => setShowReviewFeedback(prev => ({...prev, [question.question_number]: false}))}
                                    aria-label="Close feedback"
                                  >
                                    ×
                                  </button>
                                </div>
                                <textarea
                                  className="ai-review-feedback-textarea"
                                  value={aiReviewFeedback[question.question_number]}
                                  onChange={(e) => setAiReviewFeedback(prev => ({
                                    ...prev,
                                    [question.question_number]: e.target.value
                                  }))}
                                  placeholder="AI review feedback will appear here. You can edit this feedback before applying it."
                                />
                                <div className="ai-review-actions">
                                  <button
                                    className="apply-suggestions-btn"
                                    onClick={() => handleApplyFeedback(question.question_number)}
                                    disabled={isSaving[question.question_number]}
                                  >
                                    {isSaving[question.question_number] ? (
                                      <span className="ai-loading-message">
                                        <span className="spinner"></span>
                                        Applying feedback...
                                      </span>
                                    ) : (
                                      'Apply These Suggestions'
                                    )}
                                  </button>
                                  <button
                                    className="done-reviewing-btn"
                                    onClick={() => setShowReviewFeedback(prev => ({...prev, [question.question_number]: false}))}
                                  >
                                    Done Reviewing
                                  </button>
                                </div>
                              </div>
                            )}

                            {currentAiSuggestion && (
                              <div className="ai-suggestion">
                                <h5>{question.question_number === '4.2' && question42Mode === 'structured' ? 'Final Experimental Design (edit as needed):' : 'AI Suggestion (editable):'}</h5>
                                <div className="suggestion-content">
                                  <textarea
                                    value={currentAiSuggestion}
                                    onChange={(e) => setAiSuggestions(prev => ({...prev, [question.question_number]: e.target.value}))}
                                    className="ai-edit-textarea"
                                    rows={6}
                                    placeholder={question.question_number === '4.2' && question42Mode === 'structured' ? 'AI will compile your structured components into a complete experimental design here. Edit as needed before saving.' : 'AI suggestion appears here - you can edit directly'}
                                  />
                                  <div className="suggestion-buttons">
                                    <button 
                                      className="primary-button"
                                      onClick={async () => {
                                        // Update React state first
                                        updateQuestionAnswer(question.question_number, currentAiSuggestion);
                                        
                                        // Save to database immediately
                                        await saveQuestionAnswer(question.question_number);
                                        
                                        // Clear AI suggestion
                                        setAiSuggestions(prev => ({...prev, [question.question_number]: ''}));
                                        
                                        // For Question 4.2, stay in building phase and clear components for next experiment
                                        if (question.question_number === '4.2' && question42Mode === 'structured') {
                                          // Explicitly keep in building phase - don't auto-advance to final
                                          setQuestion42State('building');
                                          // Clear structured components to ready for next experiment
                                          clearStructuredComponents(question.question_number);
                                        } else if (question.question_number === '4.2') {
                                          // For free text mode, just stay in building phase
                                          setQuestion42State('building');
                                        }
                                      }}
                                    >
                                      Use This Answer
                                    </button>
                                    <button 
                                      className="secondary-button"
                                      onClick={() => setAiSuggestions(prev => ({...prev, [question.question_number]: ''}))}
                                    >
                                      Dismiss
                                    </button>
                                  </div>
                                </div>
                              </div>
                            )}
                            
                          </div>
                        </div>
                        </React.Fragment>
                      );
                    })}
                    
                  </div>
                </div>
              ) : (
                <div className="no-protocol">
                  <h2>Select a Protocol</h2>
                  <p>Choose an existing protocol from the sidebar or create a new one to begin.</p>
                </div>
              )}
            </div>
          </div>
        )}
      </main>

      {/* Floating Feedback Button - only when logged in */}
      {authState.isAuthenticated && currentView !== 'home' && (
        <button
          className="feedback-button-floating"
          onClick={() => setShowFeedbackModal(true)}
          title="Send feedback"
          aria-label="Send feedback"
        >
          💬
        </button>
      )}

      {/* Feedback Modal */}
      <FeedbackModal
        isOpen={showFeedbackModal}
        onClose={() => setShowFeedbackModal(false)}
        currentProtocolId={currentProtocol?.id}
        currentSection={typeof currentSection === 'number' ? currentSection.toString() : undefined}
        currentQuestion={undefined}
        apiBaseUrl={API_BASE_URL}
        getAuthHeaders={getAuthHeaders}
        isAdmin={authState.user?.is_admin}
        onOpenDashboard={() => setCurrentView('dashboard')}
      />

      {/* Structured Builder Overlay Screen */}
      {showStructuredBuilder && (
        <div className="structured-builder-overlay">
          <div className="structured-builder-screen">
            <div className="builder-header">
              <h2>🔨 Structured Experimental Design Builder</h2>
              <p>Build your experimental design step by step. Design one experiment at a time, then add more experiments as needed.</p>
            </div>
            
            <div className="builder-content">
              {/* Current Saved Experiments Display */}
              {sectionAnswers['4.2'] && sectionAnswers['4.2'].answer && sectionAnswers['4.2'].answer.trim() && sectionAnswers['4.2'].answer !== 'test. This didnt fill out.' && (
                <div className="saved-experiments-section">
                  <div className="saved-header">
                    <h4>📄 Previously Saved Experiments ({(() => {
                      const experimentCount = (sectionAnswers['4.2'].answer.match(/EXPERIMENT \d+:/g) || []).length;
                      return experimentCount > 0 ? `${experimentCount} Experiment${experimentCount > 1 ? 's' : ''}` : 'Content';
                    })()})</h4>
                    <button 
                      className="secondary-button small"
                      onClick={() => setShowExamples(prev => ({...prev, ['4.2-builder-saved']: !prev['4.2-builder-saved']}))}
                    >
                      {showExamples['4.2-builder-saved'] ? 'Hide' : 'Show'} Saved Text
                    </button>
                  </div>
                  {showExamples['4.2-builder-saved'] && (
                    <div className="saved-content">
                      <p className="saved-text">{sectionAnswers['4.2'].answer}</p>
                    </div>
                  )}
                </div>
              )}
              
              {/* Structured Components */}
              <div className="structured-components">
                <div className="component-section">
                  <h5>1. Hypothesis</h5>
                  <textarea
                    value={structuredComponents['4.2']?.hypothesis || ''}
                    onChange={(e) => updateStructuredComponent('4.2', 'hypothesis', e.target.value)}
                    placeholder="Clearly state your experimental hypothesis..."
                    rows={2}
                    className="component-input"
                  />
                </div>
                <div className="component-section">
                  <h5>2. Experimental Groups</h5>
                  <textarea
                    value={structuredComponents['4.2']?.groups || ''}
                    onChange={(e) => updateStructuredComponent('4.2', 'groups', e.target.value)}
                    placeholder="List your experimental groups (e.g., Control, Treatment 1, Treatment 2)..."
                    rows={3}
                    className="component-input"
                  />
                </div>
                <div className="component-section">
                  <h5>3. Timeline & Procedures</h5>
                  <textarea
                    value={structuredComponents['4.2']?.timeline || ''}
                    onChange={(e) => updateStructuredComponent('4.2', 'timeline', e.target.value)}
                    placeholder="Describe the experimental timeline and key procedures (Week 1: baseline, Week 2-4: treatment, etc.)..."
                    rows={4}
                    className="component-input"
                  />
                </div>
                <div className="component-section">
                  <h5>4. Treatment Details</h5>
                  <textarea
                    value={structuredComponents['4.2']?.treatment || ''}
                    onChange={(e) => updateStructuredComponent('4.2', 'treatment', e.target.value)}
                    placeholder="Specify doses, routes, volumes, frequencies for all treatments..."
                    rows={3}
                    className="component-input"
                  />
                </div>
              </div>
              
              {/* AI Assistance Section */}
              <div className="builder-ai-section">
                <div className="ai-buttons">
                  {!aiSuggestions['4.2'] && (
                    <button 
                      className="primary-button"
                      onClick={() => getQuestionAiAssistance('4.2')}
                      disabled={isSaving['4.2'] || !Object.values(structuredComponents['4.2'] || {}).some(val => val.trim())}
                    >
                      {isSaving['4.2'] ? '🤔 Thinking...' : 'Get AI Assistance'}
                    </button>
                  )}
                </div>
                
                {aiSuggestions['4.2'] && (
                  <div className="ai-suggestion">
                    <h5>AI Compiled Experimental Design (edit as needed):</h5>
                    <div className="suggestion-content">
                      <textarea
                        value={aiSuggestions['4.2']}
                        onChange={(e) => setAiSuggestions(prev => ({...prev, ['4.2']: e.target.value}))}
                        className="ai-edit-textarea"
                        rows={6}
                        placeholder="AI will compile your structured components into a complete experimental design here. Edit as needed before saving."
                      />
                      <div className="suggestion-buttons">
                        <button 
                          className="primary-button"
                          onClick={async () => {
                            // APPEND experiment instead of replacing
                            const existingAnswer = sectionAnswers['4.2']?.answer || '';
                            const newExperiment = aiSuggestions['4.2'];
                            
                            // Append with proper spacing
                            const combinedAnswer = existingAnswer.trim() 
                              ? `${existingAnswer.trim()}\n\n${newExperiment}`
                              : newExperiment;
                            
                            // Update React state with appended content
                            updateQuestionAnswer('4.2', combinedAnswer);
                            
                            // CRITICAL: Save to database immediately
                            await saveQuestionAnswer('4.2');
                            
                            // Clear AI suggestion and components for next experiment
                            setAiSuggestions(prev => ({...prev, ['4.2']: ''}));
                            clearStructuredComponents('4.2');
                          }}
                          disabled={isSaving['4.2']}
                        >
                          {isSaving['4.2'] ? '💾 Saving...' : 'Add This Experiment'}
                        </button>
                        <button 
                          className="secondary-button"
                          onClick={() => setAiSuggestions(prev => ({...prev, ['4.2']: ''}))}
                        >
                          Dismiss
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            <div className="builder-footer">
              <div className="builder-actions">
                <button 
                  className="secondary-button"
                  onClick={() => {
                    setShowStructuredBuilder(false);
                    setAiSuggestions(prev => ({...prev, ['4.2']: ''}));
                  }}
                >
                  Cancel
                </button>
                <button 
                  className="primary-button large"
                  onClick={() => {
                    setShowStructuredBuilder(false);
                    setQuestion42Mode('free_text');  // Switch to free text mode
                    setQuestion42State('building');   // Go to building phase for final AI review
                    setAiSuggestions(prev => ({...prev, ['4.2']: ''}));
                  }}
                >
                  ✅ Finalize Structured Builder
                </button>
              </div>
              <p className="builder-footer-hint">
                Click "Finalize Structured Builder" to return to the main form with your completed experimental design.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;